/* eslint-disable no-case-declarations */
import Cookies from "js-cookie";
import moment from "moment";

import { LeaseStatusEnum } from "../pages/Loans/v1/LoanFieldsData";

import { ObjectType } from "../types/tableTypes";

import { isFalsyButNotZero } from "./Validations";

// import { axiosClient } from "./utility";
import {
  finalPricingTables,
  labelFieldAdjustmentMapping,
  labelFieldBridgePriceMapping,
  labelFieldPricingMapping,
} from "./mappings";
import { postDownloadCondo } from "../services/network";
import { SizingTypeValue } from "../types/enums";
import { prePayTypeToMonthsMap } from "./v2/common";
import { getOverriddenTestResults } from "../components/waivers/utils";

export const filterLoanTypes: any = {
  "Bridge Loan": { label: "Bridge", key: "bridgeLoan" },
  "Investor DSCR": { label: "DSCR", key: "investorDSCR" },
};

export declare enum LoanStages {
  pre = "PRE",
  post = "POST",
  fes = "FES",
  ps = "PS",
  tv = "TOORVAL",
}

const LoanStage = {
  pre: "PRE",
  post: "POST",
  fes: "FES",
  ps: "PS",
  tv: "TOORVAL",
};
export enum LoanSizerEnum {
  oneFourFamily = "1-4 Family",
  multifamilyMixedUse = "Multifamily/MixedUse",
  groundUp = "Ground-Up",
}

export const rehabTypes = ["Fix & Flip", "Stabilized", "Rehab", "Fix&Flip"];

export const isString = (value: any) => {
  return typeof value === "string" || value instanceof String;
};

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const sanitizeCurrency = (text: string) => {
  try {
    const result = text?.replace(/\.$/, "");
    return currencyFormatter.format(
      Number.parseFloat(result?.replace(/[^0-9.]/g, ""))
    );
  } catch (e) {
    return text;
  }
};

export function isILP() {
  return window.location.pathname.startsWith("/internal/loan");
}

export const filterKeyOrgMap: { [key: string]: string } = {
  "loan.loanType.raw": "loanTypeName",
  "loan.loanState.raw": "status",
  "loan.loanDetailId.tradeId.raw": "toorakTradeID",
  "loan.loanStage.raw": "loanStage",
  "properties.propertyinfo.propertyType.raw": "propertyType",
  "loan.loanInfo.loanPurpose.raw": "loanPurpose",
  "originator.accountName.raw": "originator",
  "loan.loanInfo.recourseString.raw": "recourseString",
  "loan.loanInfo.crossCollaterlization": "crossCollaterlization",
  "loan.loanEconomics.accrualType.raw": "accrualType",
  "loan.loanEconomics.interestRateAsOfCutOffDate": "interestRate",
  "loan.loanEconomics.totalBudgetAmount.raw": "totalBudgetAmount",
  "loan.loanDetailId.loanId.raw": "toorakLoanId",
  "loan.loanInfo.primaryLoanId.raw": "originalLoanId",
};

export const filterKeyLatMap: { [key: string]: string } = {
  "loan.loanInfo.takeoutPartner.raw": "takeoutPartner",
  "loan.loanType.raw": "loanTypeName",
  "loan.loanState.raw": "status",
  "loan.loanStage.raw": "loanStage",
  "properties.propertyinfo.propertyType.raw": "propertyType",
  "loan.loanInfo.loanPurpose.raw": "loanPurpose",
  "originator.accountName.raw": "originator",
  "loan.loanInfo.recourseString.raw": "recourseString",
  "loan.loanInfo.crossCollaterlization": "crossCollaterlization",
  "loan.loanEconomics.accrualType.raw": "accrualType",
  "loan.loanDetailId.loanId.raw": "toorakLoanId",
  activeInteractions: "activeInteractions",
  "loan.loanInfo.primaryLoanId.raw": "originalLoanId",
  "loan.ddReportStatus.raw": "ddReportStatus",
  "loan.loanDetailId.loanSummaryId.teamIndiaTapetoFile.raw":
    "teamIndiaTapetoFile",
  "loan.loanDetailId.loanConfigId.tapeToFile.raw": "tapeToFile",
  "result.loanResult.riskScores.totalAggregateRisk": "riskScore",
  "result.loanResult.riskScores.riskBucket.raw": "riskBucket",
  "result.loanResult.loanCharacterisations.needEnhancedReview.raw":
    "needEnhancedReview",
};

export const filterKeyILPMap: { [key: string]: string } = {
  "originator.accountName.raw": "originator",
  "properties.propertyinfo.propertyType.raw": "propertyType",
  "loan.loanInfo.loanPurpose.raw": "loanPurpose",
  "loan.loanProcessType.raw": "loanProcessType",
  "loan.loanType.raw": "loanTypeName",
  "loan.roleOfAssignee.raw": "roleOfAssignee",
  "loan.assignedTo.raw": "assignedTo",
  "loan.workflowState.raw": "workflowState",
  "loan.tapeToFileStatus.raw": "tapeToFileStatus",
  "loan.loanInfo.toorakProduct.raw": "toorakProduct",
  "loan.ddReportStatus.raw": "ddReportStatus",
  "tasks.reconcile.taskStatus.raw": "task_status_buckets",
  "tasks.tag.taskStatus.raw": "task_status_buckets",
  "tasks.split.taskStatus.raw": "task_status_buckets",
  "tasks.review.taskStatus.raw": "task_status_buckets",
  "tasks.reconcile.assigneePartyId.raw": "assignee_party_id_buckets",
  "tasks.tag.assigneePartyId.raw": "assignee_party_id_buckets",
  "tasks.review.assigneePartyId.raw": "assignee_party_id_buckets",
  "loan.loanDetailId.loanSummaryId.teamIndiaTapetoFile.raw":
    "teamIndiaTapetoFile",
  "loan.loanDetailId.loanConfigId.tapeToFile.raw": "tapeToFile",
  "result.loanResult.riskScores.totalAggregateRisk": "riskScore",
  "result.loanResult.riskScores.riskBucket.raw": "riskBucket",
  "result.loanResult.loanCharacterisations.needEnhancedReview.raw":
    "needEnhancedReview",
};

export const filterKeyApraisalMap: { [key: string]: string } = {
  "appraisal.appraisal.loanId.raw": "loanId",
  "loan.loanState.raw": "status",
  "loan.loanDetailId.toorakLoanId.raw": "toorakLoanId",
  "appraisal.appraisal.createdBy.raw": "createdBy",
  "appraisal.appraisal.updatedBy.raw": "updatedBy",
  "appraisal.appraisal.submittedBy.raw": "submittedBy",
  "appraisal.appraisal.reviewedBy.raw": "reviewedBy",
  "originator.accountName.raw": "originator",
  "properties.propertyinfo.valuationSource.raw": "valuationSource",
};

const adjustmentKeysToBeExcluded = ["totalMarketDataDeductions"];

// Inserts a "," every 3 digits from the right (e.g. "1234567" -> "1,234,567").
// A leading "-" (Number(...).toFixed() on a negative value) is preserved but
// not counted toward the grouping. Written as a plain loop rather than the
// equivalent-looking /\B(?=(\d{3})+(?!\d))/g replace — that lookahead-based
// pattern is flagged as vulnerable to super-linear backtracking, since a
// non-digit character anywhere in the (attacker-reachable, in principle)
// input forces the engine to retry the (\d{3})+ repetition at every prior
// position before giving up.
function addThousandsSeparators(digits: string): string {
  const isNegative = digits.startsWith("-");
  const unsigned = isNegative ? digits.slice(1) : digits;
  let grouped = "";
  for (let i = 0; i < unsigned.length; i++) {
    if (i > 0 && (unsigned.length - i) % 3 === 0) {
      grouped += ",";
    }
    grouped += unsigned[i];
  }
  return isNegative ? `-${grouped}` : grouped;
}

export function sanitizeData(
  x: string | number,
  isCurrency?: boolean,
  fixNumber?: number
) {
  const toFixNumber = fixNumber ?? 2;
  if (!isNaN(+x)) {
    const returnValue = Number(x)?.toFixed(toFixNumber).split(".");
    if (x && isCurrency) {
      return `$ ${addThousandsSeparators(returnValue[0] ?? "")}.${returnValue[1]}`;
    }
    return x;
  }
  return x;
}

export function getValue(value1: any, value2: any) {
  const returnValue = value1 || value2;
  return returnValue ?? null;
}

export function getFormattedDate(date: any) {
  return date
    ? new Date(date)
        ?.toLocaleDateString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        ?.split("/")
        ?.reverse()
        ?.join("-")
    : null;
}

export function formatEscalationTimestamp(dateInput: any) {
  const date = new Date(dateInput);
  if (!dateInput || isNaN(date.getTime())) return "";

  const datePart = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
    date.getDate(),
  ).padStart(2, "0")}/${date.getFullYear()}`;

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const meridiem = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;

  return `${datePart} | ${hours}:${minutes}${meridiem}`;
}

function hasOnlySpaces(input: string) {
  return /^\s*$/.test(input);
}

export function getAutoCalculatedResults(key: string, property?: any, loanTypeData?: any) {
   switch (key) {
    case "costBasis":
      return getCostBasisValue(property?.propertyEconomics, loanTypeData) ??
      null;
    default:
      return null;
  }
}

function getCostBasisValue(
  propertyEconomics: ObjectType,
  fetchData: ObjectType
) {
  let value: string | number = "";
  if (fetchData?.propertyType === "Multifamily/MixedUse" || fetchData?.isMultifamily) {
    value =
      Number(propertyEconomics?.borrowerPaidCostToDate) +
      Number(propertyEconomics?.purchasePrice);
  } else if (rehabTypes.includes(fetchData?.loanType)) {
    value =
      ((Number(propertyEconomics?.closingCosts?.replace("%", "")) + 100) *
        Number(propertyEconomics?.purchasePrice)) /
      100;
  }
  if (!isNaN(value as number)) {
    return value;
  }
  return "";
}

// The following helpers each isolate one section of the request body built
// by normalizeReqBody. Extracted purely to reduce that function's cognitive
// complexity — behavior is unchanged from the original inline code.

function resolveReqBodyForeignNational(body: ObjectType) {
  let foreignNational = body?.borrowerInformation?.immigrationStatus;

  if (body?.borrowerInformation?.borrowerGuarantor === "Yes") {
    if (
      body?.borrowerInformation?.immigrationStatus.includes("Foriegn") ||
      body?.borrowerInformation?.immigrationStatus === ""
    ) {
      foreignNational = "Yes";
    }
  } else if (body?.borrowerInformation?.borrowerGuarantor === "No") {
    foreignNational = "No";
  }
  return foreignNational;
}

function resolveReqBodyExperience(body: ObjectType) {
  let experience =
    body?.borrowerInformation?.experience ||
    body?.borrowerInformation?.propertiesCompletedWithinLast36Months ||
    body?.borrowerInformation?.borrowerGUCExperience;
  if (hasOnlySpaces(experience) || experience?.includes(".") || !experience) {
    experience = null;
  }
  return experience;
}

// Shared by both the BRIDGE and 30Year borrower blocks below — same
// blank/decimal-guard ternary used for loanTerm and borrowerProceeds.
function nullIfBlankOrDecimal(value: any) {
  return hasOnlySpaces(value) || value?.includes(".") ? null : value ?? null;
}

// Shared by both the BRIDGE and 30Year loanUserMap blocks below.
function resolveExperienceFieldOrZero(value: any) {
  return hasOnlySpaces(value) || value?.includes(".") ? 0 : value;
}

// Shared by both the BRIDGE and 30Year loanUserMap blocks below.
function resolvePartyTypeFromBorrowerType(borrowerType: any) {
  return borrowerType === "Entity" ? "account" : "person";
}

function buildBridgePropertyEntry(
  propDetail: ObjectType,
  propIdx: number,
  fetchData?: ObjectType
) {
  if (!propDetail) {
    return undefined;
  }
  const { propertyEconomics, propertyInformation, propertyLocation } =
    propDetail;
  let closingCost = "0";
  if (propertyEconomics?.closingCosts) {
    closingCost = (
      +propertyEconomics?.closingCosts?.split("%")[0] / 100
    ).toFixed(4);
  }
  const costBasisValue = getCostBasisValue(
    propertyEconomics,
    fetchData as ObjectType
  );
  return {
    propertyId: propIdx,
    propertyInfo: {
      isPropertyPurchasedLast2Years:
        propertyInformation?.propertyPurchasedWithinLast2Years ??
        null,
      numberOfUnits: propertyInformation?.numberOfUnits
        ? hasOnlySpaces(propertyInformation?.numberOfUnits) ||
          propertyInformation?.numberOfUnits?.includes(".")
          ? null
          : +propertyInformation?.numberOfUnits
        : 1,
      propertyType: propertyInformation?.propertyType ?? "",
      changeInUseCase:
        propertyInformation?.expansionOrChangeOfUseCase ?? "",
      expansion:
        propertyInformation?.expansion ?? "",
      valuationSource: propertyInformation?.valuationSource,
      isEntitledLand:
        propertyInformation?.entitledAsOfRightDevelopment ?? null,
      appraisalReportedNotOrNcf:
        propertyEconomics?.appraisalStabilizedNOI ?? null,
      percentOccupied: propertyInformation?.percentOccupied
        ? propertyInformation?.percentOccupied / 100
        : null,
    },
    propertyLocation: {
      city: propertyLocation?.city?.replace(",", ""),
      state: propertyLocation?.state?.replace(",", ""),
      postalCode: propertyLocation?.postalCode,
      address: propertyLocation?.address ?? "",
      propertyDesignation: propertyLocation?.propertyDesignation ?? null,
    },
    propertyEcon: {
      assignmentFees: +propertyEconomics?.assignmentFee,
      grossPotentialRent:
        propertyEconomics?.estimatedRentPerYearUponCompletion?.toString(),
      costBasisAfterCostIncurred: costBasisValue,
      closingCost,
      originalAsIsAppraisalValue:
        propertyEconomics?.originalAsIsAppraisalValue ||
        propertyEconomics?.orgAppraisalValue ||
        propertyEconomics?.asIsValue,
      originalAsRepairedAppraisedValue:
        propertyEconomics?.asRepairedValueARV?.toString() ||
        propertyEconomics?.afterRepairValue ||
        "0",
      purchasePrice:
        propertyEconomics?.purchasePrice?.toString() ?? null,
      grossPotentialRate:
        propertyEconomics?.grossOriginationRate ||
        propertyEconomics?.estimatedRentPerYearUponCompletion ||
        "0",
      purchaseDate: getFormattedDate(propertyEconomics?.purchaseDate),
      grossCondoSelloutValue:
        propertyEconomics?.grossCondoSelloutValue ?? null,
      borrowerPaidCosts:
        propertyEconomics?.borrowerPaidCostToDate ?? null,
      annualPropertyTaxes:
        propertyEconomics?.annualPropertyTaxes ?? null,
      insurance: propertyEconomics?.annualInsurance ?? null,
      marketRentPerMonth:
        propertyEconomics?.grossPotentialRentMonthly ?? null,
      annualHOAFee: propertyEconomics?.annualHOAFee ?? null,
    },
  };
}

function computeBridgeRehabAmountBrl(body: ObjectType, softCosts: any) {
  if (
    isFalsyButNotZero(+body?.loanEconomics?.hardCosts) &&
    isFalsyButNotZero(+softCosts)
  ) {
    return +body?.loanEconomics?.hardCosts + +softCosts;
  }
  return null;
}

function computeBridgeOriginationPoints(body: ObjectType) {
  let totalOriginationAndDiscountPoints: any = null;

  if (body?.loanEconomics?.orgBrokerFee) {
    const orgBrokerFee = (
      +body?.loanEconomics?.orgBrokerFee.replace("%", "") / 100
    ).toFixed(6);
    totalOriginationAndDiscountPoints = orgBrokerFee;
  }

  if (body?.loanEconomics?.totalOrgAndDiscountPoints) {
    const orgBrokerFee = (
      +body?.loanEconomics?.totalOrgAndDiscountPoints.replace("%", "") /
      100
    ).toFixed(6);
    totalOriginationAndDiscountPoints = orgBrokerFee;
  }

  return totalOriginationAndDiscountPoints;
}

function resolveBridgeInterestRate(body: ObjectType) {
  return !isNaN(
    +body?.loanEconomics?.interestRate ||
      +body?.loanEconomics?.originalInterestRate
  )
    ? (
        (+body?.loanEconomics?.interestRate ||
          +body?.loanEconomics?.originalInterestRate) / 100
      ).toFixed(6)
    : null;
}

function buildBridgeLoanDetail(
  fetchData: ObjectType | undefined,
  partyDetails: ObjectType | undefined,
  loanDetails: any
) {
  return {
    originatorPartyId:
      fetchData?.orgId || partyDetails?.orgId || "",
    fundingType:
      loanDetails?.fundingType ||
      partyDetails?.orgFundingType ||
      "",
  };
}

function buildBridgeLoanEcon(
  body: ObjectType,
  softCosts: any,
  rehabAmountBrl: any,
  totalOriginationAndDiscountPoints: any
) {
  return {
    hardCost: isFalsyButNotZero(+body?.loanEconomics?.hardCosts)
      ? +body?.loanEconomics?.hardCosts
      : null,
    softCost: isFalsyButNotZero(softCosts) ? +softCosts : null,
    totalBudgetAmount:
      (Number(body?.loanEconomics?.totalRehabBudget) ||
        Number(body?.loanEconomics?.totalBudget) ||
        rehabAmountBrl) ??
      0,
    totalMaxLoanAmount:
      Number(
        body?.loanEconomics?.originalLoanAmount ||
          body?.loanEconomics?.initialLoanAmount ||
          0
      ) + Number(body?.loanEconomics?.financedBudget ?? 0),
    originalLoanAmount:
      body?.loanEconomics?.originalLoanAmount?.toString() ||
      body?.loanEconomics?.initialLoanAmount?.toString() ||
      null,
    totalOriginationAndDiscountPoints,
    interestReserve: body?.loanEconomics?.interestReserve || 0,
    rehabAmountBrl,
    financedBudgetAmount:
      body?.loanEconomics?.financedBudget || null,
    interestRate: resolveBridgeInterestRate(body),
    includeOutOfPocketBudgetInARLTV:
      body?.loanEconomics?.includeOutOfPocketBudgetARLTV ?? true,
  };
}

function buildBridgeLoanInfo(
  body: ObjectType,
  type: string,
  loanId: string | any,
  propType?: string
) {
  return {
    loanStructure: body?.loanInformation?.loanStructure,
    product: body?.loanInformation?.product || "RENTAL",
    toorakProduct:
      body?.loanInformation?.toorakProduct ||
      propType === "GroundUp"
        ? "Ground-Up"
        : "Rehab",
    loanType:
      body?.loanInformation?.loanType || type?.toUpperCase(),
    loanStage: body?.loanInformation?.loanStage || "FES",
    pledgeOfEquity:
      body?.loanInformation?.pledgeOfEquity === "No" ? "N" : "Y",
    cashOutFlag: body?.loanInformation?.cashOutFlag,
    loanPurpose: body?.loanInformation?.loanPurpose ?? "",
    exitStrategy: body?.loanInformation?.exitStrategy,
    loanId,
    loanTerm: nullIfBlankOrDecimal(body?.loanInformation?.loanTerm),
    irFundingSource: body?.loanInformation?.irFundingSource ?? null,
    isPermitApprovalsAndFullPlansInPlace:
      body?.loanInformation?.permitApprovalsAndFullPlans ?? null,
  };
}

function buildBridgeLoanUserMap(
  body: ObjectType,
  experience: any,
  foreignNational: any
) {
  return [
    {
      isPrimary: true,
      loanUserSequence: "1",
      loanUserType: "Borrower",
      originalCreditScoreMedian:
        body?.borrowerInformation?.qualifyingCreditScore ||
        body?.borrowerInformation?.originalCreditScore ||
        body?.borrowerInformation?.creditScoreActual,
      experience,
      inState: body?.borrowerInformation?.inState ?? null,
      borrowerGUCExperience: resolveExperienceFieldOrZero(
        body?.borrowerInformation?.borrowerGUCExperience
      ),
      heavyRehabExperience: resolveExperienceFieldOrZero(
        body?.borrowerInformation?.heavyRehabExperience
      ),
      customer: {
        partyType: resolvePartyTypeFromBorrowerType(
          body?.borrowerInformation?.borrowerType
        ),
        foreignNationalString: foreignNational || null,
      },
    },
  ];
}

function buildBridgeReqBody(params: {
  body: ObjectType;
  properties: ObjectType[];
  fetchData?: ObjectType;
  partyDetails?: ObjectType;
  loanDetails?: any;
  type: string;
  loanId: string | any;
  propType?: string;
  experience: any;
  foreignNational: any;
  rehabAmountBrl: any;
  totalOriginationAndDiscountPoints: any;
  softCosts: any;
}) {
  const {
    body,
    properties,
    fetchData,
    partyDetails,
    loanDetails,
    type,
    loanId,
    propType,
    experience,
    foreignNational,
    rehabAmountBrl,
    totalOriginationAndDiscountPoints,
    softCosts,
  } = params;
  return {
    loanFact: {
      loan: {
        loanDetail: buildBridgeLoanDetail(fetchData, partyDetails, loanDetails),
        loanEcon: buildBridgeLoanEcon(
          body,
          softCosts,
          rehabAmountBrl,
          totalOriginationAndDiscountPoints
        ),
        loanInfo: buildBridgeLoanInfo(body, type, loanId, propType),
        loanUserMap: buildBridgeLoanUserMap(body, experience, foreignNational),
      },
      properties,
    },
  };
}

function applyMultifamilyMixedUseOverride(
  reqBody: any,
  chipsData: string[],
  propType: string | undefined,
  rehabAmountBrl: any
) {
  if (
    (chipsData ?? [])?.includes("Multifamily/Mixed Use") &&
    propType !== "GroundUp"
  ) {
    reqBody.loanFact.loan.loanEcon.totalBudgetAmount =
      rehabAmountBrl || 0;
  }
}

function applyGroundUpOverride(
  reqBody: any,
  body: ObjectType,
  propType: string | undefined
) {
  if (propType === "GroundUp") {
    const value =
      +body?.loanEconomics?.hardCosts +
      (+body?.loanEconomics?.softCosts ||
        +body?.loanEconomics?.softCostsCap15Rehab);
    reqBody.loanFact.loan.loanEcon.totalBudget = value;
    reqBody.loanFact.loan.loanEcon.totalBudgetAmount = value;
  }
}

function applyOriginatorCategoryBucket(
  reqBody: any,
  partyDetails?: ObjectType
) {
  if (partyDetails?.orgBucketCategory) {
    // add if originator category bucket is present
    reqBody.loanFact.loan.loanDetail.originatorCategoryBucket =
      partyDetails?.orgBucketCategory;
  }
}

function applyBridgeOverriddenTestResults(
  reqBody: any,
  loanDetails: any,
  loanId: any
) {
  const overriddenTestResultsBridge = getOverriddenTestResults(
    loanDetails?.waiverDetails,
    loanId,
  );
  if (Object.keys(overriddenTestResultsBridge).length) {
    reqBody.resultObject = {
      ...reqBody.resultObject,
      loanResult: {
        ...reqBody.resultObject?.loanResult,
        [loanId]: {
          ...reqBody.resultObject?.loanResult?.[loanId],
          ...overriddenTestResultsBridge,
        },
      },
    };
  }
}

function resolveThirtyYearNumberOfUnits(propertyInformation: ObjectType) {
  if (!propertyInformation?.numberOfUnits) {
    return 1;
  }
  if (
    hasOnlySpaces(propertyInformation?.numberOfUnits) ||
    propertyInformation?.numberOfUnits?.includes(".")
  ) {
    return 0;
  }
  if (propertyInformation?.numberOfUnits) {
    return +propertyInformation?.numberOfUnits;
  }
  return 1;
}

function buildUnitEntries(unitInformation: any, propIdx: number) {
  const units: ObjectType[] = [];
  if (!Array.isArray(unitInformation)) {
    return units;
  }
  unitInformation.forEach((unitInfo: ObjectType) => {
    const returnObj = {
      propertyUnitId: propIdx + 1,
      leaseStatus:
        unitInfo?.rentalCharacterization?.toString() ?? null,
      marketRentMonthly: unitInfo?.monthlyMarketRent ?? null,
      currentLeaseTermMonths:
        unitInfo?.currentLeaseTermsInMonths?.includes(".") ||
        hasOnlySpaces(unitInfo?.currentLeaseTermsInMonths)
          ? null
          : +unitInfo?.currentLeaseTermsInMonths,
      inPlaceLeaseRentMonthly:
        unitInfo?.inPlaceLeaseRentMonthly || "",
      // mostRecentLeaseRentMonthly:
      //   unitInfo?.mostRecentLeaseRentMonthly || null,
      unitOccupancy: unitInfo?.rentalCharacterization?.includes(
        "Occupied"
      )
        ? 1
        : 0,
    };
    units.push(returnObj);
  });
  return units;
}

function build30YearPropertyEntry(
  propDetail: ObjectType,
  propIdx: number,
  defaultSelectedDataView: string
) {
  const {
    propertyEconomics,
    propertyInformation,
    propertyLocation,
    unitInformation,
  } = propDetail;

  let closingCost = "0";
  if (propertyEconomics?.closingCosts) {
    closingCost = (
      +propertyEconomics?.closingCosts?.split("%")[0] / 100
    ).toFixed(4);
  }
  const units = buildUnitEntries(unitInformation, propIdx);
  const currentProp: ObjectType = {
    propertyId: propIdx + 1,
    propertySummary: {
      monthlyTaxAmount: null,
      monthlyInsuranceAmount: null,
      escrowTaxes: null,
      escrowInsurance: null,
    },
    propertyInfo: {
      numberOfUnits: resolveThirtyYearNumberOfUnits(propertyInformation),
      propertyType:
        (propertyLocation?.propertyType ||
          propertyInformation?.propertyType) ??
        "",
      postRehabSquareFootage: 0,
      squareFootage:
        propertyInformation?.propertySqftFootage?.replaceAll(",", ""),
      preRehabSquareFootage:
        propertyInformation?.propertySqftFootage?.replaceAll(",", ""),
      propertyAcquisitionDate: propertyInformation?.originalDateOfAcquisition ? getFormattedDate(
        propertyInformation?.originalDateOfAcquisition
      ) : null,
      mostRecentPropertyValuationDate: getFormattedDate(
        propertyInformation?.recentPropValuationDate
      ),
      isPurchasedWithDebt: propertyInformation?.propertyWithDebt,
      isPropertyRecentlyRenovated: propertyInformation?.isPropertyRecentlyRenovated
    },
    propertyLocation: {
      city: propertyLocation?.city,
      state: propertyLocation?.state,
      postalCode: propertyLocation?.postalCode,
      propertyDesignation: propertyLocation?.propertyDesignation ?? null,
    },
    propertyEcon: {
      costBasisAfterCostIncurred:
        (+closingCost + 1) * propertyEconomics?.purchasePrice,
      closingCost,
      additionalEligibleCost: null,
      originalAsIsAppraisalValue: propertyEconomics?.asIsValue || null,
      purchasePrice:
        propertyEconomics?.propertyAcquisitionPrice ||
        propertyEconomics?.purchasePrice ||
        null,
      thirdPartyValuationStatus:
        propertyEconomics?.thirdPartyValuationStatus ?? null,
      thirdPartyValuation: propertyEconomics?.thirdPartyPopertyValue,
      annualPropertyTaxes: propertyEconomics?.annualPropertyTaxes,
      annualHazardInsurance: propertyEconomics?.annualHazardInsurance,
      annualFloodInsurance: propertyEconomics?.annualFloodInsurance,
      annualHOAFee: propertyEconomics?.annualHOAFee,
      acquisitionPrice:
        propertyEconomics?.propertyAcquisitionPrice || null,
      decliningMarkets: propertyEconomics?.decliningMarkets ?? null,
      verifiedCostsAndImprovements: propertyEconomics?.verifiedCostsAndImprovements ? propertyEconomics?.verifiedCostsAndImprovements :null,
    },
  };
  if (defaultSelectedDataView !== "minimumDataView") {
    currentProp.units = units;
  }
  return currentProp;
}

function resolveThirtyYearArmField(
  isRateTypeFixed: boolean,
  value: any,
  defaultValue: any = null
) {
  // Matches the original `isRateTypeFixed ? null : value || defaultValue`
  // ternary used for each ARM-related field. `value` is evaluated eagerly
  // by callers (instead of lazily inside the ternary), which is safe here
  // because every call site passes either a plain property access or a
  // call to the pure, side-effect-free getFormattedDate — so eager
  // evaluation cannot change behavior or throw.
  if (isRateTypeFixed) {
    return null;
  }
  return value || defaultValue;
}

function buildThirtyYearLoanDetail(
  body: ObjectType,
  fetchData?: ObjectType,
  partyDetails?: ObjectType
) {
  return {
    originatorPartyId:
      body?.orgId || fetchData?.orgId || partyDetails?.orgId || "",
    // fundingType: partyDetails?.orgFundingType || "",
  };
}

function buildThirtyYearLoanEcon(body: ObjectType, isRateTypeFixed: boolean) {
  return {
    rateType: body?.loanEconomics?.rateType ?? "Fixed",
    originalLoanAmount:
      body?.loanEconomics?.originalLoanAmount?.toString() ||
      body?.loanEconomics?.initialLoanAmount?.toString() ||
      body?.loanEconomics?.borrowerRequested?.toString(),
    prePaymentPenaltyMonths:
      body?.loanEconomics?.prepaymentPenaltyTypeMonths ?? null,
    prepayPenaltyType: body?.loanEconomics?.prepayPenaltyType,
    interestRate: +(
      (body?.loanEconomics?.originalInterestRate ||
        body?.loanEconomics?.noteRate) / 100
    ).toFixed(6),
    interestOnlyPeriod: body?.loanEconomics?.ioPeriod || 0,
    armType: resolveThirtyYearArmField(
      isRateTypeFixed,
      body?.loanEconomics?.armType
    ),
    armIndex: resolveThirtyYearArmField(
      isRateTypeFixed,
      body?.loanEconomics?.armIndex,
      "1 Month Term SOFR"
    ),
    // grossArmMargin: body?.loanEconomics?.grossArmMargin ?? null,
    initialRateAdjustmentDate: resolveThirtyYearArmField(
      isRateTypeFixed,
      getFormattedDate(body?.loanEconomics?.initialRateAdjustmentDate)
    ),
    initialPayAdjustmentDate: resolveThirtyYearArmField(
      isRateTypeFixed,
      getFormattedDate(body?.loanEconomics?.initialPayAdjustmentDate)
    ),
    // initialPeriodicCap: body?.loanEconomics?.initialPeriodicCap ?? null,
    // subsequentPeriodicCap: body?.loanEconomics?.subsequentPeriodicCap ?? null,
    // lifeRateCap: body?.loanEconomics?.lifeRateCap ?? null,
    // lifeTimeFloor: body?.loanEconomics?.lifeTimeFloor ?? null,
  };
}

function buildThirtyYearLoanInfo(
  body: ObjectType,
  type: string,
  loanId: string | any,
  borrowerProceeds: any
) {
  return {
    product: body?.loanInformation?.product || "RENTAL",
    toorakProduct: body?.loanInformation?.toorakProduct || "Rehab",
    loanType:
      body?.loanInformation?.loanType || type?.toUpperCase(),
    loanStage: body?.loanInformation?.loanStage || "FES",
    loanId,
    borrowerProceeds: nullIfBlankOrDecimal(borrowerProceeds),
    predominantState:
      body?.loanInformation?.predominantState ?? null, // else null
    predominantPropertyType:
      body?.loanInformation?.predominantPropertyType === ""
        ? null
        : body?.loanInformation?.predominantPropertyType,
    originalMaturityDate: null,
    expectedClosingDate: body?.loanInformation?.expectedClosingDate ? getFormattedDate(
      body?.loanInformation?.expectedClosingDate
    ) : null,
    // loanPurpose: body?.loanInformation?.loanPurpose ?? "",
    condoEligibility:
      body?.loanInformation?.condoEligibility || "N/A",
    loanTerm: nullIfBlankOrDecimal(body?.loanInformation?.loanTerm),
    crossCollaterlization:
      body?.loanInformation?.crossCollaterlization ?? "N",
    creditEvent: body?.loanInformation?.creditEvent ?? "No",
    originationDate: body?.loanInformation?.expectedClosingDate ? getFormattedDate(
      body?.loanInformation?.expectedClosingDate
    ) : null,
  };
}

function buildThirtyYearLoanUserMap(
  body: ObjectType,
  experience: any,
  foreignNational: any
) {
  return [
    {
      isPrimary: true,
      loanUserSequence: "1",
      loanUserType: "Borrower",
      originalCreditScoreMedian:
        body?.borrowerInformation?.qualifyingCreditScore ||
        body?.borrowerInformation?.originalCreditScore ||
        body?.borrowerInformation?.creditScoreActual,
      experience,
      borrowerGUCExperience: resolveExperienceFieldOrZero(
        body?.borrowerInformation?.borrowerGUCExperience
      ),
      heavyRehabExperience: resolveExperienceFieldOrZero(
        body?.borrowerInformation?.heavyRehabExperience
      ),
      customer: {
        partyType: resolvePartyTypeFromBorrowerType(
          body?.borrowerInformation?.borrowerType
        ),
        foreignNationalString: foreignNational || null,
      },
    },
  ];
}

function buildThirtyYearReqBody(params: {
  body: ObjectType;
  properties: ObjectType[];
  type: string;
  loanId: string | any;
  experience: any;
  foreignNational: any;
  borrowerProceeds: any;
  isRateTypeFixed: boolean;
  fetchData?: ObjectType;
  partyDetails?: ObjectType;
}) {
  const {
    body,
    properties,
    type,
    loanId,
    experience,
    foreignNational,
    borrowerProceeds,
    isRateTypeFixed,
    fetchData,
    partyDetails,
  } = params;
  return {
    loanFact: {
      loan: {
        loanDetail: buildThirtyYearLoanDetail(body, fetchData, partyDetails),
        loanEcon: buildThirtyYearLoanEcon(body, isRateTypeFixed),
        loanInfo: buildThirtyYearLoanInfo(body, type, loanId, borrowerProceeds),
        loanUserMap: buildThirtyYearLoanUserMap(body, experience, foreignNational),
      },
      properties,
    },
  };
}

function applyLoanPurposeToResult(resultObj: any, loanId: any, body: ObjectType) {
  if (body?.loanInformation?.loanPurpose) {
    resultObj.loanResult[loanId] = {
      ...resultObj.loanResult[loanId],
      loanCharacterization: {
        finalLoanCharacterization:
          body?.loanInformation?.loanPurpose ?? null,
      },
    };
  }
}

function applyRentalCharacterizationAggregate(
  resultObj: any,
  loanId: any,
  propertyDetails: ObjectType[]
) {
  if (propertyDetails[0]?.propertyInformation?.rentalCharacterization) {
    resultObj.loanResult[loanId] = {
      ...resultObj.loanResult[loanId],
      loanEconomics: {
        ...(resultObj.loanResult[loanId]?.loanEconomics ?? {}),
        propertyRentalCharacterizationAggregate:
          propertyDetails[0]?.propertyInformation
            ?.rentalCharacterization ?? "",
      },
    };
  }
}

function buildMinimumDataViewTempPropBody(propertyDetails: ObjectType[]) {
  const tempPropBody: ObjectType = {};
  propertyDetails.forEach((propDetail: ObjectType, propIdx: number) => {
    if (
      propDetail?.propertyInformation?.monthlyPropertyGrossRent ||
      propDetail?.propertyInformation?.grossRent
    ) {
      tempPropBody[propIdx + 1] = {
        loanEconomics: {
          ...tempPropBody[propIdx + 1]?.loanEconomics,
          monthlyPropertyGrossRent:
            propDetail?.propertyInformation?.monthlyPropertyGrossRent ||
            propDetail?.propertyInformation?.grossRent,
        },
      };
    }
    if (propDetail?.propertyInformation?.rentalCharacterization) {
      tempPropBody[propIdx + 1] = {
        loanEconomics: {
          ...tempPropBody[propIdx + 1]?.loanEconomics,
          percentOccupied:
            propDetail?.propertyInformation?.rentalCharacterization?.includes(
              "Occupied"
            )
              ? 1.0
              : 0,
        },
      };
    }
  });
  return tempPropBody;
}

function applyFirstTimeHomeBuyer(reqBody: any, body: ObjectType) {
  if (body?.borrowerInformation?.firstTimeHomeBuyer) {
    reqBody.loanFact.loan.loanUserMap[0].firstTimeHomeBuyer =
      body?.borrowerInformation?.firstTimeHomeBuyer;
  }
}

function applyRequestedAsIsLTV(resultObj: any, loanId: any, body: ObjectType) {
  if (body?.loanEconomics?.requestedAsIsLTV) {
    resultObj.loanResult[loanId] = {
      ...resultObj.loanResult[loanId],
      loanFeatures: {
        ...(resultObj.loanResult[loanId].loanFeatures ?? {}),
        originalLtv: +body?.loanEconomics?.requestedAsIsLTV / 100,
      },
    };
  }
}

function applyRequestedLTPP(resultObj: any, loanId: any, body: ObjectType) {
  if (body?.loanEconomics?.requestedLTPP) {
    resultObj.loanResult[loanId] = {
      ...resultObj.loanResult[loanId],
      loanFeatures: {
        ...(resultObj.loanResult[loanId]?.loanFeatures ?? {}),
        originalLtc: +body?.loanEconomics?.requestedLTPP / 100,
      },
    };
  }
}

function applyDscrToResult(resultObj: any, loanId: any, body: ObjectType) {
  if (body?.loanEconomics?.dscr) {
    resultObj.loanResult[loanId] = {
      ...resultObj.loanResult[loanId],
      loanEconomics: {
        ...(resultObj.loanResult[loanId]?.loanEconomics ?? {}),
        dscr: body?.loanEconomics?.dscr ?? null,
      },
    };
  }
}

function applyPropertiesResults(reqBody: any, tempPropBody: ObjectType) {
  if (Object.keys(tempPropBody).length) {
    reqBody.resultObject = {
      ...reqBody.resultObject,
      propertiesResults: tempPropBody,
    };
  }
}

function applyMinimumDataViewAdjustments(
  reqBody: any,
  resultObj: any,
  loanId: any,
  body: ObjectType,
  propertyDetails: ObjectType[]
) {
  applyRentalCharacterizationAggregate(resultObj, loanId, propertyDetails);
  const tempPropBody = buildMinimumDataViewTempPropBody(propertyDetails);
  applyFirstTimeHomeBuyer(reqBody, body);
  // if (body?.loanInformation?.loanPurpose) {
  //   resultObj.loanResult[loanId] = {
  //     ...resultObj.loanResult[loanId],
  //     loanCharacterization: {
  //       finalLoanCharacterization:
  //         body?.loanInformation?.loanPurpose ?? null,
  //     },
  //   };
  // }

  applyRequestedAsIsLTV(resultObj, loanId, body);
  applyRequestedLTPP(resultObj, loanId, body);
  applyDscrToResult(resultObj, loanId, body);

  applyPropertiesResults(reqBody, tempPropBody);
  // if (Object.keys(resultObj.loanResult[loanId]).length) {
  //   reqBody.resultObject = {
  //     ...reqBody.resultObject,
  //     ...resultObj,
  //   };
  // }
}

function applyOverriddenThirtyYearTestResults(
  resultObj: any,
  loanId: any,
  loanDetails: any
) {
  const overriddenTestResults30Year = getOverriddenTestResults(
    loanDetails?.waiverDetails,
    loanId,
  );
  Object.entries(overriddenTestResults30Year).forEach(([testType, tests]) => {
    resultObj.loanResult[loanId] = {
      ...resultObj.loanResult[loanId],
      [testType]: {
        ...resultObj.loanResult[loanId]?.[testType],
        ...tests,
      },
    };
  });
}

export const normalizeReqBody = (
  body: ObjectType,
  propertyDetails: ObjectType[],
  type: string,
  loanId: string | any,
  defaultSelectedDataView: string,
  chipsData: string[],
  propType?: string,
  partyDetails?: ObjectType,
  fetchData?: ObjectType,
  loanDetails?: any
) => {
  let reqBody: any = {};

  try {
    const properties: ObjectType[] = [];

    const foreignNational = resolveReqBodyForeignNational(body);
    const experience = resolveReqBodyExperience(body);

    const softCosts =
      body?.loanEconomics?.softCosts ||
      body?.loanEconomics?.softCostsCap15Rehab;

    switch (type) {
      case "BRIDGE": {
        propertyDetails?.forEach((propDetail: ObjectType, propIdx: number) => {
          const currentProp = buildBridgePropertyEntry(
            propDetail,
            propIdx,
            fetchData
          );
          if (currentProp) {
            properties.push(currentProp);
          }
        });

        const rehabAmountBrl = computeBridgeRehabAmountBrl(body, softCosts);
        const totalOriginationAndDiscountPoints =
          computeBridgeOriginationPoints(body);

        reqBody = buildBridgeReqBody({
          body,
          properties,
          fetchData,
          partyDetails,
          loanDetails,
          type,
          loanId,
          propType,
          experience,
          foreignNational,
          rehabAmountBrl,
          totalOriginationAndDiscountPoints,
          softCosts,
        });

        applyMultifamilyMixedUseOverride(
          reqBody,
          chipsData,
          propType,
          rehabAmountBrl
        );
        applyGroundUpOverride(reqBody, body, propType);
        applyOriginatorCategoryBucket(reqBody, partyDetails);
        applyBridgeOverriddenTestResults(reqBody, loanDetails, loanId);
        break;
      }
      case "30Year": {
        propertyDetails.forEach((propDetail: ObjectType, propIdx: number) => {
          properties.push(
            build30YearPropertyEntry(
              propDetail,
              propIdx,
              defaultSelectedDataView
            )
          );
        });

        const borrowerProceeds =
          body?.loanInformation?.borrowerProceeds ||
          body?.loanInformation?.cashOutRefinance;
        const isRateTypeFixed = body?.loanEconomics?.rateType === "Fixed";
        reqBody = buildThirtyYearReqBody({
          body,
          properties,
          type,
          loanId,
          experience,
          foreignNational,
          borrowerProceeds,
          isRateTypeFixed,
          fetchData,
          partyDetails,
        });

        const resultObj: any = {
          loanResult: {
            [loanId]: {},
          },
        };


        applyLoanPurposeToResult(resultObj, loanId, body);

        if (defaultSelectedDataView === "minimumDataView") {
          applyMinimumDataViewAdjustments(
            reqBody,
            resultObj,
            loanId,
            body,
            propertyDetails
          );
        }

        applyOverriddenThirtyYearTestResults(resultObj, loanId, loanDetails);

        if (Object.keys(resultObj.loanResult[loanId]).length) {
            reqBody.resultObject = {
              ...reqBody.resultObject,
              ...resultObj,
            };
        }
        break;
      }
      default:
        break;
    }
    return reqBody;
  } catch (err) {
    console.error(err);
  }
};

const getAdjustmentsData = (
  body: ObjectType,
  isDSCR: boolean,
  shouldMultiplyBy100: boolean = true
) => {
  const tableBody: any = [];
  const multiplyBy = shouldMultiplyBy100 ? 100 : 1;
  Object.keys(body)
    ?.filter((str: string) => !adjustmentKeysToBeExcluded?.includes(str))
    ?.forEach((ele: any) => {
      const key = labelFieldAdjustmentMapping[ele];
      const value =
        typeof +body[ele] === "number" &&
        !isNaN(+body[ele]) &&
        body[ele] &&
        body[ele] !== null
          ? `${(+body[ele] * multiplyBy)?.toFixed(4)} %`
          : null;
      const excludeLeverageKeys = [
        "Price Adjustment ",
        "Final Toorak Price ",
        "Total Adjustments ",
        "Total Market Data Deductions",
      ];

      if (value) {
        if (isDSCR) {
          if (!excludeLeverageKeys.includes(key)) {
            tableBody.push([key, value, value]);
          } else return;
        } else {
          tableBody.push([key, value]);
        }
      }
    });
  return tableBody;
};

const getBridgePricingData = (
  body: ObjectType,
  isGroundUp: boolean,
  pricingAdjustmentOverrides?: Record<string, string>
) => {
  const returnArray: any = [];
  labelFieldBridgePriceMapping.forEach((field: ObjectType) => {
    if (!field.isVisible(isGroundUp)) return;

    // Same override-first, always-shown pattern as the DSCR Pricing card's
    // getPricingData — a manually-overridden row always takes its
    // overridden value, and every configured row shows (defaulted to 0)
    // even when the live value is absent/falsy, since rows are editable.
    const override = pricingAdjustmentOverrides?.[field.label];
    if (override !== undefined) {
      returnArray.push([field.label, override]);
      return;
    }

    const value = getPercentageValue(body[field.field] ?? 0);
    returnArray.push([field.label, value]);
  });
  return returnArray;
};

const getFinalPricingTable = (body: any) => {
  const returnArr: any = [];
  Object.keys(finalPricingTables as ObjectType).forEach((key: string) => {
    const valueKey: string = finalPricingTables[key];
    if (body?.[valueKey]) {
      returnArr.push([
        key,
        valueKey === "actualInterestRate"
          ? `${(body?.[valueKey] * 100)?.toFixed(4)} %`
          : body?.[valueKey],
      ]);
    }
  });
  return returnArr;
};
const getPricingData = (
  body: ObjectType,
  isDSCR: boolean,
  pricingAdjustmentOverrides?: Record<string, string>
) => {
  const returnArr: any = [];
  labelFieldPricingMapping?.forEach((field: ObjectType) => {
    // A manually-overridden row always takes its overridden value —
    // regardless of what the API naturally computed for it.
    const override = pricingAdjustmentOverrides?.[field.label];
    if (override !== undefined) {
      returnArr.push([field.label, override]);
      return;
    }

    // Every configured adjustment row always shows, even when the live
    // value is absent/falsy (defaulting to 0) — rows are editable now, so
    // hiding a row entirely (the old behavior) would hide the one thing a
    // user might want to edit.
    const rawValue = body?.[field?.field];
    // Parsed to a number (not just truthiness-checked) before formatting as
    // a percentage — rawValue may arrive as a numeric string, which has no
    // .toFixed, and a live 0 must still format as "0.0000 %" now that a
    // falsy value no longer means "drop the row" (it used to be invisible
    // either way, since the row wasn't shown at all).
    const parsedValue =
      rawValue !== undefined && rawValue !== null ? Number(rawValue) : 0;
    const value: any =
      field.fieldType === "percentage" && !isNaN(parsedValue)
        ? field?.convertToPercentage
          ? `${(parsedValue * 100).toFixed(4)} %`
          : `${parsedValue.toFixed(4)} %`
        : sanitizeData(rawValue ?? 0, false);

    returnArr.push([field.label, value]);
  });
  return returnArr;
};

const getSum = (a: any, b: any) => {
  if (!isNaN(a) && !isNaN(b)) {
    return +a + +b;
  }
  return 0;
};

const isValidNumericPair = (reqValue: number, adjustedValue: number) =>
  typeof +reqValue === "number" &&
  typeof +adjustedValue === "number" &&
  !Number.isNaN(+reqValue) &&
  !Number.isNaN(+adjustedValue);

const formatLowerTableValue = (reqValue: number, toBeFixed: boolean) => {
  const isNegative = +reqValue < 0;
  if (toBeFixed) {
    return isNegative
      ? Number(reqValue)?.toFixed(2)
      : `-${Number(reqValue)?.toFixed(2)}`;
  }
  return isNegative ? Number(reqValue) : `-${Number(reqValue)}`;
};

const formatHigherTableValue = (reqValue: number, toBeFixed: boolean) =>
  toBeFixed ? `+${Number(reqValue)?.toFixed(2)}` : `+${Number(reqValue)}`;

export const getTableValue = (
  reqValue: number,
  adjustedValue: number,
  toBeFixed: boolean
) => {
  if (
    !isFalsyButNotZero(reqValue) ||
    !isValidNumericPair(reqValue, adjustedValue)
  ) {
    return reqValue ?? "";
  }

  if (+reqValue < +adjustedValue) {
    return formatLowerTableValue(reqValue, toBeFixed);
  }
  if (+reqValue > +adjustedValue) {
    return formatHigherTableValue(reqValue, toBeFixed);
  }

  return reqValue ?? "";
};

const getFormattedNumber = (value: any) => {
  if (!Number.isNaN(Number(value)) && value) {
    return Number(value)?.toFixed(2);
  }
  return value || "-";
};

const getProceedsData = (
  body: ObjectType,
  loanData: any,
  type: string,
  isMutlifamily: boolean
) => {
  let tableBody: any = [];
  switch (type) {
    case "Rehab":
    case "Fix & Flip":
    case "Stabilized":
      tableBody = [
        {
          id: "max",
          label: "max",
          data: [
            "Max",
            getFormattedNumber(body?.maxLeverage?.maxInitialLoanFunding) ?? "",
            getFormattedNumber(body?.finalLeverage?.rehabAmount) ?? "",
            getFormattedNumber(body?.maxLeverage?.maxTotalMaxLoanAmount) ?? "",
          ],
        },
        {
          id: "adjustedMax",
          label: "adjustedMax",
          data: [
            "Adjusted Max",
            getFormattedNumber(body?.finalLeverage?.initialLoanFunding),
            getFormattedNumber(body?.finalLeverage?.rehabAmount),
            getFormattedNumber(body?.finalLeverage?.totalMaxLoanAmount),
          ],
        },
        {
          id: "requested",
          label: "requested",
          data: [
            "Requested",
            getTableValue(
              loanData?.loanEconomics?.initialLoanAmount,
              body?.finalLeverage?.initialLoanFunding,
              true
            ) ?? "",
            getTableValue(
              isMutlifamily
                ? getSum(
                    loanData?.loanEconomics?.financedBudget ?? 0, 0
                  )
                : loanData?.loanEconomics?.financedBudget ?? 0,
              Number(body?.finalLeverage?.rehabAmount),
              true
            ) ?? "",
            getTableValue(
              getSum(
                loanData?.loanEconomics?.initialLoanAmount ?? 0,
                isMutlifamily
                  ? getSum(
                      loanData?.loanEconomics?.financedBudget ?? 0 , 0
                    )
                  : loanData?.loanEconomics?.financedBudget ?? 0
              ),
              // body?.loanFeatures?.requestedTotalMaxLoanAmount,
              body?.finalLeverage?.totalMaxLoanAmount,
              true
            ) ?? "",
          ],
        },
      ];
      break;
    case "Ground Up":
      const reqRehab = getSum(
        loanData?.loanEconomics?.financedBudget,
        0
      );
      const initialPlusRehab = getSum(
        body?.finalLeverage?.rehabAmount,
        body?.finalLeverage?.initialLoanFunding
      );
      tableBody = [
        {
          id: "initialProceeds",
          label: "initialProceeds",
          data: [
            "Initial Proceeds",
            getTableValue(
              loanData?.loanEconomics?.initialLoanAmount,
              body?.finalLeverage?.initialLoanFunding,
              true
            ) ?? "",
            Number(body?.finalLeverage?.initialLoanFunding)?.toFixed(2) ?? "",
          ],
        },
        {
          id: "rehabAmount",
          label: "rehabAmount",
          data: [
            "Financed Budget",
            getTableValue(reqRehab, body?.finalLeverage?.rehabAmount, true) ??
              "",
            Number(body?.finalLeverage?.rehabAmount)?.toFixed(2) ?? "",
          ],
        },
        {
          id: "initialLoanPlusRehabAmount",
          label: "initialLoanPlusRehabAmount",
          data: [
            "Initial Loan + Financed Budget",
            getTableValue(  
              getSum(loanData?.loanEconomics?.initialLoanAmount, reqRehab),
              initialPlusRehab,
              true
            ) ?? "",
            Number(
              getSum(
                body?.finalLeverage?.rehabAmount,
                body?.finalLeverage?.initialLoanFunding
              )
            ).toFixed(2) ?? "",
          ],
        },

        {
          id: "interestReserve",
          label: "interestReserve",
          data: [
            "Interest Reserve",
            getTableValue(
              loanData?.loanEconomics?.interestReserve,
              body?.finalLeverage?.interestReserveProceeds,
              true
            ) ?? "",
            Number(body?.finalLeverage?.interestReserveProceeds)?.toFixed(2) ??
              "",
          ],
        },
        {
          id: "totalMaxLoanAmount",
          label: "totalMaxLoanAmount",
          data: [
            "Total Max Loan Amount",
            getTableValue(
              getSum(
                getSum(loanData?.loanEconomics?.initialLoanAmount, reqRehab),
                loanData?.loanEconomics?.interestReserve ?? 0
              ),
              body?.finalLeverage?.totalMaxLoanAmount,
              true
            ) ?? "",
            Number(body?.finalLeverage?.totalMaxLoanAmount)?.toFixed(2) ?? "",
          ],
        },
      ];
      break;
    default:
      break;
  }
  return {
    tableBody,
  };
};

// Formats a percentage-eligible value that is below 100, honoring the
// nonPercent/shouldMultiply flags exactly as getPercentageValue's original
// inline branching did. Extracted purely to reduce getPercentageValue's
// cognitive complexity — behavior is unchanged.
function formatSubHundredPercentage(
  tempValue: any,
  tempNonPercent: boolean,
  tempToFixedVal: number,
  tempShouldMultiply: boolean
): string {
  if (!tempShouldMultiply) {
    return `${(+tempValue).toFixed(tempToFixedVal)} %`;
  }
  return tempNonPercent
    ? (+tempValue * 100).toFixed(tempToFixedVal)
    : `${(+tempValue * 100).toFixed(tempToFixedVal)} %`;
}

export const getPercentageValue = (
  value: any,
  nonPercent?: boolean,
  toFixedVal?: number,
  shouldMultiply?: boolean
) => {
  let returnValue: any = null;
  const tempNonPercent = nonPercent ?? false;
  const tempToFixedVal = toFixedVal ?? 2;
  const tempShouldMultiply = shouldMultiply ?? true;
  let tempValue = value;

  let isSymbol: any = null;
  if (/[+-]/.test(tempValue)) {
    isSymbol = `${tempValue}`?.charAt(0);
    tempValue = `${tempValue}`?.slice(1);
  }

  if (isFalsyButNotZero(tempValue)) {
    if (typeof tempValue === "string" && isNaN(+tempValue)) {
      returnValue = tempValue;
    } else if (+tempValue < 100) {
      returnValue = formatSubHundredPercentage(
        tempValue,
        tempNonPercent,
        tempToFixedVal,
        tempShouldMultiply
      );
    } else {
      returnValue = `${tempValue} %`;
    }
  }
  return isSymbol ? `${isSymbol}${returnValue}` : returnValue ?? "";
};

export const getLeverageTableBody = (
  loanStage: string,
  body: any,
  loanData?: any,
  // Manual overrides for the "Adjusted Max" row only (the sole editable row
  // in the Leverages card — see LeverageTable.tsx), keyed by the column's
  // tableHeader label ("ltvAsIs" / "ltvARV" / "ltc" / "lttc"). A present
  // override always wins over the API-computed value, same override-first
  // rule getPricingData already uses for the Pricing card, so a manually
  // overridden column keeps showing even if the live recalculation lands on
  // a falsy/zero value.
  adjustedMaxOverrides?: Record<string, string>
) => {
  switch (loanStage) {
    case "Rehab":
    case "Fix & Flip":
    case "Stabilized":
      return [
        {
          id: "max",
          label: "max",
          borderColor: "safronBorder",
          data: [
            "Max",
            getPercentageValue(body?.maxLeverage?.maxLTVAsIs),
            getPercentageValue(body?.maxLeverage?.maxLTVARV),
            getPercentageValue(body?.maxLeverage?.maxLTCOne),
          ],
        },
        {
          id: "adjustedMax",
          label: "adjustedMax",
          borderColor: "skyBlueBorder",
          data: [
            "Adjusted Max",
            adjustedMaxOverrides?.ltvAsIs ??
              getPercentageValue(body?.finalLeverage?.afterAdjustmentMaxLTVAsIs),
            adjustedMaxOverrides?.ltvARV ??
              getPercentageValue(body?.finalLeverage?.afterAdjustmentMaxARV),
            adjustedMaxOverrides?.ltc ??
              getPercentageValue(body?.finalLeverage?.afterAdjustmentMaxLTC),
          ],
        },
        {
          id: "requested",
          label: "requested",
          borderColor: "blueBorder",
          data: [
            "Requested",
            getPercentageValue(
              getTableValue(
                body?.loanFeatures?.originalAsIsLtv,
                Number(body?.finalLeverage?.afterAdjustmentMaxLTVAsIs),
                false
              )
            ),
            getPercentageValue(
              getTableValue(
                body?.loanFeatures?.originalAsIsRepairedLtv,
                Number(body?.finalLeverage?.afterAdjustmentMaxARV),
                false
              )
            ),
            getPercentageValue(
              getTableValue(
                body?.loanFeatures?.originalLtc,
                Number(body?.finalLeverage?.afterAdjustmentMaxLTC),
                false
              )
            ),
          ],
        },
        // {
        //   id: "toorakFinalLeverage",
        //   label: "toorakFinalLeverage",
        //   borderColor: "greenBorder",
        //   data: [
        //     "Toorak Final Leverage",
        //     getPercentageValue(body?.finalLeverage?.finalLTVAsIs),
        //     getPercentageValue(body?.finalLeverage?.finalARV),
        //     getPercentageValue(body?.finalLeverage?.finalLTC),
        //   ],
        // },
      ];
    case "MultiFamily":
    case "Ground Up":
      return [
        {
          id: "max",
          label: "max",
          borderColor: "safronBorder",
          data: [
            "Max",
            getPercentageValue(body.maxLeverage.maxLTVAsIs),
            getPercentageValue(body.maxLeverage.arvLTVAsCompleted),
            getPercentageValue(body.maxLeverage.maxLTCAsIs),
            getPercentageValue(body.maxLeverage.maxLttc),
          ],
        },
        {
          id: "adjustedMax",
          label: "adjustedMax",
          borderColor: "skyBlueBorder",
          data: [
            "Adjusted Max",
            adjustedMaxOverrides?.ltvAsIs ??
              getPercentageValue(body.finalLeverage.afterAdjustmentMaxLTVAsIs),
            adjustedMaxOverrides?.ltvARV ??
              getPercentageValue(body.finalLeverage.afterAdjustmentMaxARV),
            adjustedMaxOverrides?.ltc ??
              getPercentageValue(body.finalLeverage.afterAdjustmentMaxLTC),
            adjustedMaxOverrides?.lttc ??
              getPercentageValue(body.finalLeverage.afterAdjustmentMaxLTTC),
          ],
        },
        {
          id: "requested",
          label: "requested",
          borderColor: "blueBorder",
          data: [
            "Requested",
            getPercentageValue(
              getTableValue(
                body.loanFeatures.originalAsIsLtv,
                Number(body.finalLeverage.afterAdjustmentMaxLTVAsIs),
                false
              )
            ),
            getPercentageValue(
              getTableValue(
                body.loanFeatures.arvLtvAsCompleted,
                Number(body.finalLeverage.afterAdjustmentMaxARV),
                false
              )
            ),
            getPercentageValue(
              getTableValue(
                body.loanFeatures.originalLtc,
                Number(body.finalLeverage.afterAdjustmentMaxLTC),
                false
              )
            ),
            getPercentageValue(
              getTableValue(
                body.loanFeatures.originalLttc,
                Number(body.finalLeverage.afterAdjustmentMaxLTTC),
                false
              )
            ),
          ],
        },
        {
          id: "toorakFinalLeverage",
          label: "toorakFinalLeverage",
          borderColor: "greenBorder",
          data: [
            "Toorak Final Leverage",
            getPercentageValue(body?.finalLeverage?.finalLTVAsIs),
            getPercentageValue(body?.finalLeverage?.finalARV),
            getPercentageValue(body?.finalLeverage?.finalLTC),
          ],
        },
      ];
    default:
      return [
        {
          id: "max",
          label: "max",
          borderColor: "safronBorder",
          data: [
            "Max",
            getPercentageValue(body?.maxLeverage?.maxAsIsLTV),
            getPercentageValue(body?.maxLeverage?.maxLTC), //maxLTC
          ],
        },
        {
          id: "adjustedMax",
          label: "adjustedMax",
          borderColor: "greenBorder",
          data: [
            "Adjusted Max",
            adjustedMaxOverrides?.ltvAsIs ??
              getPercentageValue(body?.toorakProceeds?.toorakAdjustedAsIsLtv),
            adjustedMaxOverrides?.ltc ??
              getPercentageValue(body?.toorakProceeds?.toorakAdjustedAsIsLtc), // toorakAdjustedAsIsLtc
          ],
        },
        {
          id: "requested",
          label: "requested",
          borderColor: "blueBorder",
          data: [
            "Requested",
            getPercentageValue(
              getTableValue(
                getValue(
                  body?.loanFeatures?.originalAsIsLtv,
                  loanData?.loanEconomics?.requestedAsIsLTV
                ),
                body?.toorakProceeds?.toorakAdjustedAsIsLtv,
                false
              )
            ),
            getPercentageValue(
              getTableValue(
                getValue(
                  body?.loanFeatures?.originalLtc,
                  loanData?.loanEconomics?.requestedLTPP
                ),
                body?.toorakProceeds?.toorakAdjustedAsIsLtc,
                false
              )
            ),
          ],
        },
      ];
  }
};

// Computes the Bridge finalRate/baseRate leverage-card fields, matching the
// original inline computation exactly (including the unused-but-preserved
// regToorakYield calculation). Extracted purely to reduce
// buildBridgeEvaluationResult's cognitive complexity — behavior unchanged.
function computeBridgeRates(
  body: ObjectType,
  dataToBeChecked: any
): { finalRate: any; baseRate: any } {
  let regToorakYield = body?.toorakInterests?.regularToorakYield
    ? body?.toorakInterests?.regularToorakYield * 100
    : 0;
  if (body?.toorakInterests?.regularToorakYield) {
    regToorakYield =
      regToorakYield * 100 + (+dataToBeChecked?.loanEconomics?.brokerFee || 0);
  }
  // (body?.toorakInterests?.regularToorakYield ? (getPercentageValue(regToorakYield, false, 4, false)) : null) ||
  const finalRate =
    (body?.toorakInterests?.finalToorakYield
      ? getPercentageValue(body?.toorakInterests?.finalToorakYield, false, 4)
      : null) ||
    (getPercentageValue(
      dataToBeChecked?.loanEconomics?.interestRate ||
        dataToBeChecked?.loanEconomics?.originalInterestRate,
      false,
      4,
      false
    ) ??
      null);
  const baseRate = body?.toorakInterests?.regularToorakYield
    ? getPercentageValue(body?.toorakInterests?.regularToorakYield, false, 4)
    : null;
  return { finalRate, baseRate };
}

const buildBridgeEvaluationResult = (opts: {
  body: ObjectType;
  dataToBeChecked: any;
  fetchData: ObjectType;
  leverageAdjustedMaxOverrides?: Record<string, string>;
  pricingAdjustmentOverrides?: Record<string, string>;
}) => {
  const { body, dataToBeChecked, fetchData, leverageAdjustedMaxOverrides, pricingAdjustmentOverrides } = opts;
  const { finalRate, baseRate } = computeBridgeRates(body, dataToBeChecked);
  const bridgeAdjustments = getAdjustmentsData(body?.discounts, false, true);
  return {
    leverageCards: {
      maxLoanAmount:
        sanitizeData(body.finalLeverage.totalMaxLoanAmount, true, 2) ?? "",
      finalRate: finalRate ?? "",
      baseRate: baseRate ?? "",
      priceAdjustment: getPercentageValue(
        +body?.toorakInterests?.finalToorakYield -
          +body?.toorakInterests?.regularToorakYield
      ),
      interestRate: body?.loanPricing?.interestOnlyRate.toFixed(4),
      pricingType: body?.toorakInterests?.rateSheetType,
    },
    riskScoreCards: {
      riskScore: body?.riskScores?.totalAggregateRisk,
      riskBucket: body?.riskScores?.riskBucket,
      riskScoresVersion: body?.riskScores?.riskScoresVersion,
      pd: `${(
        parseFloat(body?.riskScores?.totalAggregateRisk ?? "0.0") * 100
      ).toFixed(3)}%`,
    },
    leverages: {
      tableBody: getLeverageTableBody(
        fetchData?.loanType,
        body,
        undefined,
        leverageAdjustedMaxOverrides
      ),
    },
    leverageAdjustment: {
      isLeverageAdjustment: bridgeAdjustments?.length > 0,
      totalAdjustments: (body?.discounts?.totalMarketDataDeductions || 0) * 100, // keep as number
      totalNegative: (body?.discounts?.totalMarketDataDeductions || 0) * 100,
      totalPositive: "0.0000",
      tableBody: bridgeAdjustments,
    },
    profitAndRentYield: {
      profitability: getPercentageValue(
        body?.guidelineOutput?.profitabilityCalculation ||
          body?.guidelineOutput?.profitability,
        false,
        4
      ),
      rentYield:
        body.guidelineOutput.rentYieldCalculation ||
        body.guidelineOutput.rentYield,
    },
    proceeds: getProceedsData(
      body,
      dataToBeChecked,
      fetchData?.loanType,
      ["Mixed Use", "5+ Unit Multifamily", "Multifamily/MixedUse"].includes(
        fetchData?.propertyType ?? ""
      )
    ),
    pricing: {
      tableBody: getBridgePricingData(
        body?.toorakInterests,
        fetchData?.loanType === "Ground Up",
        pricingAdjustmentOverrides
      ),
    },
  };
};

// Builds the DSCR (30Year) leverageCards section, matching the original
// inline object exactly (including the maxLoanAmount/interestRate
// computations, which are only ever used within this section). Extracted
// purely to reduce build30YearEvaluationResult's cognitive complexity —
// behavior unchanged.
function buildDscrLeverageCards(opts: {
  body: ObjectType;
  dataToBeChecked: any;
  failedCountTests: number | null;
  originationPoints: any;
}) {
  const { body, dataToBeChecked, failedCountTests, originationPoints } = opts;
  const maxLoanAmount = isFalsyButNotZero(body?.toorakProceeds?.toorakLoanAmount)
    ? body?.toorakProceeds?.toorakLoanAmount
    : dataToBeChecked?.loanEconomics?.borrowerRequested ||
      dataToBeChecked?.loanInformation?.borrowerRequested;
  // Always sourced directly from the guideline API's response — no
  // client-side (e.g. Credit-Committee-entered loanEconomics.interestRate/
  // originalInterestRate) fallback is used.
  const interestRate = body?.loanPricing?.actualInterestRate
    ? (body?.loanPricing?.actualInterestRate * 100).toFixed(4)
    : "";

  return {
    maxLoanAmount: isFalsyButNotZero(body?.toorakProceeds?.toorakLoanAmount)
      ? `${sanitizeData(maxLoanAmount, true, 2)}`
      : null,
    finalPrice: body?.loanPricing?.finalToorakPrice,
    interestRate: interestRate ? `${interestRate} %` : "",
    dscr: body?.loanEconomics?.dscr,
    prepaymentPenaltyTypeMonths:
      dataToBeChecked?.loanEconomics?.prepaymentPenaltyTypeMonths ||
      dataToBeChecked?.loanInformation?.prepaymentPenaltyTypeMonths,
    failedTests: failedCountTests ?? null,
    prepayPenaltyType:
      (dataToBeChecked?.loanEconomics?.prepayPenaltyType ||
        dataToBeChecked?.loanInformation?.prepayPenaltyType) ??
      prePayTypeToMonthsMap?.[
        dataToBeChecked?.loanEconomics?.prepaymentPenaltyTypeMonths ||
          dataToBeChecked?.loanInformation?.prepaymentPenaltyTypeMonths ||
          0
      ],
    originationPoints:
      originationPoints?.value ?? originationPoints?.defaultValue ?? "",
  };
}

// Builds the DSCR (30Year) requiredReserves section, matching the original
// inline object exactly. Extracted purely to reduce
// build30YearEvaluationResult's cognitive complexity — behavior unchanged.
function buildDscrRequiredReserves(opts: {
  body: ObjectType;
  dataToBeChecked: any;
  selectedBuyDownOptions: any;
  failedCountTests: number | null;
}) {
  const { body, dataToBeChecked, selectedBuyDownOptions, failedCountTests } =
    opts;
  return {
    minimumRequiredReserves: body?.loanEconomics?.minimumBorrowerLiquidity
      ? `${sanitizeData(body?.loanEconomics?.minimumBorrowerLiquidity, true, 2)}`
      : selectedBuyDownOptions?.minimumBorrowerLiquidity
      ? `${sanitizeData(
          selectedBuyDownOptions?.minimumBorrowerLiquidity,
          true,
          2
        )}`
      : "",
    dscr: isFalsyButNotZero(body?.loanEconomics?.dscr)
      ? body?.loanEconomics?.dscr
      : dataToBeChecked?.loanEconomics?.dscr,
    failedTests: failedCountTests ?? null,
  };
}

const build30YearEvaluationResult = (opts: {
  body: ObjectType;
  dataToBeChecked: any;
  fetchData: ObjectType;
  failedCountTests: number | null;
  selectedBuyDownOptions: any;
  originationPoints: any;
  pricingAdjustmentOverrides?: Record<string, string>;
  leverageAdjustedMaxOverrides?: Record<string, string>;
}) => {
  const {
    body,
    dataToBeChecked,
    fetchData,
    failedCountTests,
    selectedBuyDownOptions,
    originationPoints,
    pricingAdjustmentOverrides,
    leverageAdjustedMaxOverrides,
  } = opts;
  const dscrAdjustments = getAdjustmentsData(
    body?.adjustmentsResult30Yr,
    true,
    true
  );

  return {
    leverageCards: buildDscrLeverageCards({
      body,
      dataToBeChecked,
      failedCountTests,
      originationPoints,
    }),
    leverages: {
      tableBody: getLeverageTableBody(
        fetchData?.loanType,
        body,
        dataToBeChecked,
        leverageAdjustedMaxOverrides
      ),
    },
    leverageAdjustment: {
      isLeverageAdjustment: dscrAdjustments?.length > 0,
      totalAdjustments: body?.adjustmentsResult30Yr?.totalAdjustments
        ? body?.adjustmentsResult30Yr?.totalAdjustments * 100
        : null, // keep as number
      totalNegative: body?.adjustmentsResult30Yr?.totalAdjustments
        ? body?.adjustmentsResult30Yr?.totalAdjustments * 100
        : null,
      totalPositive: "0.0000",
      tableBody: dscrAdjustments,
    },
    requiredReserves: buildDscrRequiredReserves({
      body,
      dataToBeChecked,
      selectedBuyDownOptions,
      failedCountTests,
    }),
    finalPriceTable: {
      rateAsOfDate: "",
      finalPriceRate: body?.loanPricing?.finalToorakPrice,
      totalAdjusment: body?.adjustmentsResult30Yr?.totalAdjustments
        ? `${body?.adjustmentsResult30Yr?.totalAdjustments * 100} %`
        : "",
      tableBody: getFinalPricingTable(body?.loanPricing),
    },
    pricing: {
      tableBody: getPricingData(body?.loanPricing, true, pricingAdjustmentOverrides),
    },
    proceeds: {},
  };
};

export const normalizeEvaluationResult = (
  body: ObjectType,
  loanType: string,
  loanData: ObjectType,
  fetchData: ObjectType,
  failedCountTests: number | null,
  pricingAdjustmentOverrides?: Record<string, string>,
  leverageAdjustedMaxOverrides?: Record<string, string>
) => {
  const dataToBeChecked =
    loanData?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING
      ? loanData?.quickLoanSizingData
      : loanData?.sizingType === SizingTypeValue?.DETAILED_LOAN_SIZING
      ? loanData?.detailedLoanSizingData
      : loanData;
  const currentSelectedInterestRate = loanData?.currentSelectedInterestRate;
  const selectedBuyDownOptions = body?.buyDownOptions?.find((item: any) => {
    const itemInterestRate = item?.interestRate
      ? parseFloat(item.interestRate) * 100
      : null;
    const selectedInterestRate = currentSelectedInterestRate?.interestRate
      ? parseFloat(currentSelectedInterestRate?.interestRate.replace("%", ""))
      : null;

    if (itemInterestRate === null || selectedInterestRate === null) {
      return false;
    }
    return itemInterestRate.toFixed(4) === selectedInterestRate.toFixed(4);
  });
  const originationPoints =
    loanData?.originatorAccountMappingData?.termSheetDynamicFields?.find(
      (filed: any) => filed?.field === "origination_points"
    );

  if (Object.keys(body).length) {
    switch (loanType) {
      case "BRIDGE":
        return buildBridgeEvaluationResult({
          body,
          dataToBeChecked,
          fetchData,
          leverageAdjustedMaxOverrides,
          pricingAdjustmentOverrides,
        });
      case "30Year":
        return build30YearEvaluationResult({
          body,
          dataToBeChecked,
          fetchData,
          failedCountTests,
          selectedBuyDownOptions,
          originationPoints,
          pricingAdjustmentOverrides,
          leverageAdjustedMaxOverrides,
        });
      default:
        break;
    }
    return {};
  }
};

// Resolves loanSizerType/loanType for normalizeSubmitApplicationLoanData,
// matching the original inline if/else chain exactly. Extracted purely to
// reduce that function's cognitive complexity — behavior is unchanged.
function resolveLoanTypeAndSizerType(fetchData: ObjectType): {
  loanSizerType: string | null;
  loanType: string;
} {
  if (fetchData?.productType !== "Bridge Loan") {
    return { loanSizerType: null, loanType: "30 Year Loan" };
  }
  if (fetchData?.loanType === "Ground Up") {
    return { loanSizerType: "Ground-Up", loanType: "BridgeLoan" };
  }
  if (
    ["Mixed Use", "5+ Unit Multifamily", "Multifamily/MixedUse"].includes(
      fetchData?.propertyType ?? ""
    )
  ) {
    return { loanSizerType: "Multifamily/MixedUse", loanType: "BridgeLoan" };
  }
  return { loanSizerType: "1-4 Family", loanType: "BridgeLoan" };
}

// Resolves the experience value for normalizeSubmitApplicationLoanData,
// matching the original inline computation exactly. Extracted purely to
// reduce that function's cognitive complexity — behavior is unchanged.
function resolveExperience(loanData: any): any {
  const experience =
    loanData?.borrowerInformation?.experience ||
    loanData?.borrowerInformation?.propertiesCompletedWithinLast36Months ||
    loanData?.borrowerInformation?.borrowerGUCExperience;
  return hasOnlySpaces(experience) || experience?.includes(".")
    ? null
    : experience;
}

// Resolves the foreignNational value for normalizeSubmitApplicationLoanData,
// matching the original inline if/else chain exactly. Extracted purely to
// reduce that function's cognitive complexity — behavior is unchanged.
function resolveForeignNationalStatus(loanData: any, loanType: string): any {
  let foreignNational = loanData?.borrowerInformation?.borrowerGuarantor;

  if (loanType !== "30 Year Loan") {
    return foreignNational;
  }

  foreignNational =
    loanData?.borrowerInformation?.immigrationStatus ??
    loanData?.borrowerInformation?.citizenshipStatus;
  if (loanData?.borrowerInformation?.borrowerGuarantor === "Yes") {
    if (
      loanData?.borrowerInformation?.immigrationStatus.includes("Foriegn") ||
      loanData?.borrowerInformation?.immigrationStatus === ""
    ) {
      foreignNational = "Yes";
    }
  } else if (loanData?.borrowerInformation?.borrowerGuarantor === "No") {
    foreignNational = "No";
  }
  return foreignNational;
}

// The following resolveXxx helpers each isolate one field's original inline
// expression from buildLoanEconomicsSection, matching that expression
// exactly. Extracted purely to reduce that function's cognitive complexity —
// behavior is unchanged.
function resolveOriginalLoanAmount(loanData: any): any {
  return (
    (loanData?.loanEconomics?.originalLoanAmount?.toString() ||
      loanData?.loanEconomics?.initialLoanAmount?.toString() ||
      loanData?.loanEconomics?.borrowerRequested?.toString()) ?? null
  );
}

function resolveRehabAmount(loanData: any, totalBudgetAmount: any): any {
  return (
    (loanData?.loanEconomics?.totalRehabBudget
      ? `${loanData?.loanEconomics?.totalRehabBudget}`
      : totalBudgetAmount
      ? `${totalBudgetAmount}`
      : null) ?? null
  );
}

function resolveSoftCost(loanData: any): any {
  return (
    (loanData?.loanEconomics?.softCosts ||
      loanData?.loanEconomics?.softCostsCap15Rehab) ?? null
  );
}

function resolveInterestRateAsOfCutOffDate(loanData: any): any {
  return (
    (+loanData?.loanEconomics?.interestRate ||
      +loanData?.loanEconomics?.originalInterestRate) ?? null
  );
}

function resolveTotalOriginationAndDiscountPoints(
  loanData: any,
  originationPoints: any
): any {
  return (
    (loanData?.loanEconomics?.totalOrgAndDiscountPoints ||
      loanData?.loanEconomics?.orgBrokerFee) ??
    originationPoints?.value ??
    originationPoints?.defaultValue ??
    null
  );
}

// Matches the original `isRateTypeFixed ? null : value ?? defaultValue`
// ternary used for each ARM-related field. `value` is evaluated eagerly by
// callers (instead of lazily inside the ternary), which is safe here because
// every call site passes either a plain property access or a call to the
// pure, side-effect-free getFormattedDate — so eager evaluation cannot change
// behavior or throw.
function resolveArmField(
  isRateTypeFixed: boolean,
  value: any,
  defaultValue: any = null
): any {
  if (isRateTypeFixed) {
    return null;
  }
  return value ?? defaultValue;
}

// Builds the loanEconomics section for normalizeSubmitApplicationLoanData,
// matching the original inline object exactly. Extracted purely to reduce
// that function's cognitive complexity — behavior is unchanged.
function buildLoanEconomicsSection(
  loanData: any,
  totalBudgetAmount: any,
  originationPoints: any,
  isRateTypeFixed: boolean
): ObjectType {
  return {
    totalBudgetAmount: totalBudgetAmount ? `${totalBudgetAmount}` : "0",
    originalLoanAmount: resolveOriginalLoanAmount(loanData),
    rehabAmount: resolveRehabAmount(loanData, totalBudgetAmount),
    existDebt: loanData?.loanEconomics?.exitDebt ?? null,
    softCost: resolveSoftCost(loanData),
    hardCost: loanData?.loanEconomics?.hardCosts ?? null,
    interestRateAsOfCutOffDate: resolveInterestRateAsOfCutOffDate(loanData),
    prepaymentPenaltyMonths:
      loanData?.loanEconomics?.prepaymentPenaltyTypeMonths ?? null,
    prepayPenaltyType: loanData?.loanEconomics?.prepayPenaltyType ?? null,
    rateType: loanData?.loanEconomics?.rateType ?? "Fixed",
    interestReserve: loanData?.loanEconomics?.interestReserve ?? null,
    // totalRehabBudget:
    //   (loanData?.loanEconomics?.totalRehabBudget ||
    //     +loanData?.loanEconomics?.hardCosts +
    //       (+loanData?.loanEconomics?.softCosts ||
    //         +loanData?.loanEconomics?.softCostsCap15Rehab)) ??
    //   null,
    rehabBudget: totalBudgetAmount ? `${totalBudgetAmount}` : 0,
    interestOnlyPeriod:
      loanData?.loanEconomics?.interestOnlyFlag === "No"
        ? 0
        : loanData?.loanEconomics?.ioPeriod,
    totalOriginationAndDiscountPoints: resolveTotalOriginationAndDiscountPoints(
      loanData,
      originationPoints
    ),
    includeOutOfPocketBudgetInARLTV:
      loanData?.loanEconomics?.includeOutOfPocketBudgetARLTV ?? true,
    armType: resolveArmField(isRateTypeFixed, loanData?.loanEconomics?.armType),
    armIndex: resolveArmField(
      isRateTypeFixed,
      loanData?.loanEconomics?.armIndex,
      "1 Month Term SOFR"
    ),
    // grossArmMargin: loanData?.loanEconomics?.grossArmMargin ?? null,
    initialRateAdjustmentDate: resolveArmField(
      isRateTypeFixed,
      getFormattedDate(loanData?.loanEconomics?.initialRateAdjustmentDate)
    ),
    initialPayAdjustmentDate: resolveArmField(
      isRateTypeFixed,
      getFormattedDate(loanData?.loanEconomics?.initialPayAdjustmentDate)
    ),
    // initialPeriodicCap: loanData?.loanEconomics?.initialPeriodicCap ?? null,
    // subsequentPeriodicCap: loanData?.loanEconomics?.subsequentPeriodicCap ?? null,
    // lifeRateCap: loanData?.loanEconomics?.lifeRateCap ?? null,
    // lifeTimeFloor: loanData?.loanEconomics?.lifeTimeFloor ?? null,
  };
}

// The following resolveXxx helpers each isolate one field's original inline
// expression from buildLoanInfoSection, matching that expression exactly.
// Extracted purely to reduce that function's cognitive complexity —
// behavior is unchanged.
function resolveLoanPurpose(
  loanData: any,
  autoCalculatedLoanResults: ObjectType
): any {
  return (
    (loanData?.loanInformation?.loanPurpose ||
      autoCalculatedLoanResults?.loanInformation?.loanPurpose) ?? null
  );
}

function resolveBorrowerProceedsDisplay(borrowerProceeds: any): any {
  if (hasOnlySpaces(borrowerProceeds) || borrowerProceeds?.includes(".")) {
    return null;
  }
  return borrowerProceeds === "Y" ? "Yes" : "No";
}

function resolveNoOfProperties(loanData: any): any {
  if (
    hasOnlySpaces(loanData?.loanInformation?.numberOfProperties) ||
    loanData?.loanInformation?.numberOfProperties?.includes(".")
  ) {
    return null;
  }
  return parseInt(loanData?.loanInformation?.numberOfProperties) ?? null;
}

function resolveLoanInfoOriginationDate(
  loanData: any,
  loanType: string,
  expectedClosingDate: any
): any {
  if (loanType === "30 Year Loan") {
    return expectedClosingDate;
  }
  return loanData?.loanInformation?.originationDate
    ? getFormattedDate(loanData?.loanInformation?.originationDate)
    : null;
}

function resolveLoanTermMonthly(loanData: any): any {
  return (
    (loanData?.loanInformation?.loanTerm
      ? Number(loanData?.loanInformation?.loanTerm)
      : null) ?? null
  );
}

// Builds the loanInfo section for normalizeSubmitApplicationLoanData,
// matching the original inline object exactly. Extracted purely to reduce
// that function's cognitive complexity — behavior is unchanged.
function buildLoanInfoSection(opts: {
  loanData: any;
  autoCalculatedLoanResults: ObjectType;
  borrowerProceeds: any;
  expectedClosingDate: any;
  loanType: string;
}): ObjectType {
  const {
    loanData,
    autoCalculatedLoanResults,
    borrowerProceeds,
    expectedClosingDate,
    loanType,
  } = opts;
  return {
    primaryLoanId: loanData?.loanInformation?.originatorLoanID ?? "",
    loanStructure: loanData?.loanInformation?.loanStructure ?? null,
    loanPurpose: resolveLoanPurpose(loanData, autoCalculatedLoanResults),
    cashoutFlag: loanData?.loanInformation?.cashOutFlag ?? null,
    recourseString: loanData?.loanInformation?.recourse ?? null,
    pledgeOfEquity:
      yesornToBoolean(loanData?.loanInformation?.pledgeOfEquity) ?? null,
    isPermitApprovalsAndFullPlansInPlace:
      loanData?.loanInformation?.permitApprovalsAndFullPlans ?? null,
    expectedClosingDate,
    irFundingSource: loanData?.loanInformation?.irFundingSource ?? null,
    sizerRateLockPeriod:
      loanData?.loanInformation?.sizerRateLockPeriod ?? null,
    borrowerProceeds: resolveBorrowerProceedsDisplay(borrowerProceeds),
    noOfProperties: resolveNoOfProperties(loanData),
    predominantState: loanData?.loanInformation?.predominantState || null,
    predominantPropertyType:
      loanData?.loanInformation?.predominantPropertyType || null,
    originationDate: resolveLoanInfoOriginationDate(
      loanData,
      loanType,
      expectedClosingDate
    ),
    loanTermMonthly: resolveLoanTermMonthly(loanData),
    calculatedCondoEligibility:
      loanData?.loanInformation?.condoEligibility ?? null,
    condoEligibility: loanData?.loanInformation?.condoEligibility ?? null,
    exitStrategy: loanData?.loanInformation?.exitStrategy ?? null,
    crossCollaterlization:
      yesornToBoolean(loanData?.loanInformation?.crossCollaterlization) ??
      null,
    creditEvent: loanData?.loanInformation?.creditEvent ?? "No",
    takeoutPartner: loanData?.takeOutPartner,
    takeoutPartnerStatus: loanData?.takeoutPartnerStatus,
  };
}

// Builds the single loanUserMap borrower entry for
// normalizeSubmitApplicationLoanData, matching the original inline object
// exactly. Extracted purely to reduce that function's cognitive complexity —
// behavior is unchanged.
function buildLoanUserMapEntry(
  loanData: any,
  experience: any,
  foreignNational: any
): ObjectType {
  return {
    loanUserType: "Borrower",
    loanUserSequence: "1",
    isPrimary: true,
    originalCreditScoreMedian:
      loanData?.borrowerInformation?.qualifyingCreditScore ||
      loanData?.borrowerInformation?.originalCreditScore ||
      loanData?.borrowerInformation?.creditScoreActual,
    originalCreditScoreReportDate: null,
    experience,
    inState: loanData?.borrowerInformation?.inState ?? null,
    borrowerGUCExperience:
      (hasOnlySpaces(loanData?.borrowerInformation?.borrowerGUCExperience) ||
      loanData?.borrowerInformation?.borrowerGUCExperience?.includes(".")
        ? null
        : loanData?.borrowerInformation?.borrowerGUCExperience) ?? null,
    heavyRehabExperience:
      hasOnlySpaces(loanData?.borrowerInformation?.heavyRehabExperience) ||
      loanData?.borrowerInformation?.heavyRehabExperience?.includes(".")
        ? null
        : loanData?.borrowerInformation?.heavyRehabExperience ?? null,
    ownershipOfEntity: null,
    customer: {
      accountName: "",
      accountType:
        loanData?.borrowerInformation?.borrowerType === "Entity"
          ? "BORROWER"
          : "person",
      addressList: [],
      contactList: [],
      dateOfBirth: null,
      firstName: "",
      middleName: null,
      lastName: null,
      partyType:
        loanData?.borrowerInformation?.borrowerType === "Entity"
          ? "account"
          : "person",
      foreignNationalString: foreignNational ?? null,
    },
    firstTimeHomeBuyer: loanData?.borrowerInformation?.firstTimeHomeBuyer ?? null,
  };
}

export const normalizeSubmitApplicationLoanData = (
  loanData: any,
  loanId: string,
  loanTypeId: number,
  condoEligibilityFormData: ObjectType,
  autoCalculatedLoanResults: ObjectType,
  fetchData: ObjectType,
  loanDetails?: any,
  latestTermSheetVersion?: any
) => {
  const { loanSizerType, loanType } = resolveLoanTypeAndSizerType(fetchData);

  const datas = window.location?.pathname?.split("/");
  const tempOriginatorData: string | ObjectType =
    getCookie("originatorData") ||
    localStorage?.getItem("originatorData") ||
    "{}";
  const originatorData = JSON.parse(tempOriginatorData);

  const experience = resolveExperience(loanData);

  const totalBudgetAmount =
    loanData?.loanEconomics?.totalRehabBudget ||
    +loanData?.loanEconomics?.hardCosts + +loanData?.loanEconomics?.softCosts ||
    +loanData?.loanEconomics?.hardCosts +
      +loanData?.loanEconomics?.softCostsCap15Rehab ||
    loanData?.loanEconomics?.totalBudget ||
    "0";

  const foreignNational = resolveForeignNationalStatus(loanData, loanType);

  const defaultOriginatorFee = loanData?.defaultOriginatorFee;
  const originationPoints = defaultOriginatorFee?.find(
    (item: any) => item?.field === "origination_points"
  );

  const borrowerProceeds =
    loanData?.loanInformation?.borrowerProceeds ||
    loanData?.loanInformation?.cashOutRefinance;
  const isRateTypeFixed = loanData?.loanEconomics?.rateType === "Fixed";
  const expectedClosingDate = loanData?.loanInformation?.expectedClosingDate ?
        getFormattedDate(loanData?.loanInformation?.expectedClosingDate) :
        null;
  const originationDate = loanType === "30 Year Loan" ? expectedClosingDate : null;
  const returnData: ObjectType = {
    loanId,
    loanTypeId,
    loanSizerType,
    loanType,
    loanExcelJson: "",
    onlineSizerMetaData: {
      evaluationId: loanDetails?.applicationName || loanDetails?.evaluationID,
      termSheetVersion: latestTermSheetVersion || "",
      urlMeta: {
        productType: loanDetails?.productType?.replace(" ", ""),
        loanType: loanDetails?.productType?.includes("DSCR")
          ? "dscr"
          : "bridge",
        loanUuid: datas[datas?.length - 1],
        url: window.location.href,
        applicationName: loanDetails?.applicationName,
      },
    },
    loanEconomics: buildLoanEconomicsSection(
      loanData,
      totalBudgetAmount,
      originationPoints,
      isRateTypeFixed
    ),
    loanInfo: buildLoanInfoSection({
      loanData,
      autoCalculatedLoanResults,
      borrowerProceeds,
      expectedClosingDate,
      loanType,
    }),
    loanUserMap: [buildLoanUserMapEntry(loanData, experience, foreignNational)],
    loanConfig: {},
    loanCondominium: condoEligibilityFormData,
    loanDetailId: {
      loanId: loanId,
      toorakLoanId: loanId,
      toorakLoanIdSequence: "92552",
      loanIdPrefix: loanType === "BridgeLoan" ? "BR" : "TY",
      tradeId: null,
      originatorPartyId: "",
      loanConfigId: {
        dueDiligencePartyId: null,
        sellerPartyId: null,
        spocPartyId: null,
        servicerPartyId: null,
        servicerName: "BSI",
        ttfVersionId: 1,
        tapeToFile: "No",
        rateLockRateAdjustment: null,
        created_by: originatorData?.partyId ?? "",
        updated_by: null,
        deleted_on: null,
        appraisal: null,
        streetView: null,
        zillow: null,
        truliaCrime: null,
        emailSentToOriginator: null,
        datePurchased: null,
        ruleSetVersion: null,
        latAnalystPartyId: null,
        latAnalystApproverPartyId: null,
        status: null,
        comments: null,
        toorakEligibility: null,
        custodyDocuments: null,
        appraiserList: null,
        rateLockPeriod: null,
        sizerRateLockPeriod: null,
        rateAdjustmentLockDate: null,
        closingLegalFirm: null,
        closingEscrowCompany: null,
        wireToLegalOrEscrow: null,
        enableBudgetReview: null,
        created_on: new Date(),
        updated_on: new Date(),
        loanConfigId: 128355,
        dateUploaded: new Date(),
      },
      loanSummaryId: {
        documentsSplitStatus: "Pending",
        documentsTaggingStatus: "Pending",
        ttfReviewStatus: "Pending",
        teamIndiaTapetoFile: "No",
        created_by: originatorData?.partyId ?? "", // originator ID
        updated_by: null,
        deleted_on: null,
        monthlyTaxAmount: null,
        escrowTaxes: null,
        monthsOfTaxes: null,
        escrowInsurance: null,
        monthsOfInsurance: null,
        taxesAndInsuranceCollected: null,
        MonthlyInsuranceAmount: null,
        taxes: null,
        insurance: null,
        taxesandInsuranceDue: null,
        escrowFund: null,
        lastPaymentDate: null,
        nextPaymentDue: null,
        updatedEscrowBalance: null,
        updatedTaxEscrowBalance: null,
        updatedInsuranceEscrowBalance: null,
        interestRateStepUp: null,
        daysUntil: null,
        percentageStepUp: null,
        femaSearch: null,
        escrowReconciliation: null,
        leaseStatus: null,
        seasoned: null,
        paymentHistoryandUPBChecked: null,
        escrowType: null,
        nextPaymentDueDate: null,
        escrowTaxHUD: null,
        escrowFloodInsuranceHUD: null,
        escrowHazardInsuranceHUD: null,
        escrowFloodInsurance: null,
        escrowHazardInsurance: null,
        monthsOfFloodInsurance: null,
        monthsOfHazardInsurance: null,
        annualFloodInsurance: null,
        annualHazardInsurance: null,
        propertyEscrowTax: null,
        propertyHazardInsurance: null,
        propertyFloodInsurance: null,
        escrowValuesSource: null,
        correctTaxEscrow: null,
        correctFloodInsuranceEscrow: null,
        correctHazardInsuranceEscrow: null,
        escrowTaxesAtClosing: null,
        monthsOfPropertyTaxEscrow: null,
        escrowCalculatorType: null,
        insuranceAgentFirstName: null,
        insuranceAgentLastName: null,
        insuranceAgentEmail: null,
        insuranceAgentContactNumber: null,
        insuranceExpirationDate: null,
        dataReconcilationStatus: null,
        insuranceCompany: null,
        checkListOverridenBy: null,
        created_on: new Date(),
        updated_on: new Date(),
        loanSummaryId: 126580,
      },
      discountPointPercentage:
        loanData?.currentSelectedInterestRate?.discountPoints ?? null,
      discountPointAmount:
        loanData?.currentSelectedInterestRate?.discountPointsWithDollar?.replace(
          /[$,]/g,
          ""
        ) ?? null,
      // fundingType: "normal",
      created_by: originatorData?.partyId ?? "",
      updated_by: null,
      deleted_on: null,
      isWarehouseFunded: null,
      loanProcessDetailId: 132674,
      created_on: new Date(),
      updated_on: new Date(),
      loanDetailId: 128352,
      loanLandmarks: [
        {
          name: "loan_creation",
          date: new Date(),
          value: "FES",
          createdBy: originatorData?.partyId ?? "",
          comments: null,
          stage: "FES",
          loanLandmarkId: 611002,
        },
      ],
    },
    originatorInfo: { ...originatorData },
    defaultOriginatorFee,
  };
  return returnData;
};

// Builds the propertyUnit array for a single property from its raw
// unitInformation, matching normalizeSubmitApplicationPropertyData's
// original inline forEach exactly. Extracted purely to reduce that
// function's cognitive complexity — behavior is unchanged.
function buildUnitsForProperty(
  unitInformation: any,
  propIdx: number
): ObjectType[] {
  if (!unitInformation || !Array.isArray(unitInformation)) {
    return [];
  }
  return unitInformation.map((unitInfo: ObjectType) => ({
    propertyUnitId: propIdx,
    leaseEndDate: null,
    leaseStartDate: null,
    monthlyLease: "",
    leaseDurationMorethan12Mos: "",
    leaseDurationLessthan12Mos: "",
    vacancyStatus: "",
    leaseStatus: unitInfo?.rentalCharacterization?.toString() ?? null,
    unitCategory: unitInfo?.unitCategory ?? null,
    percentageOccupied: unitInfo?.percentUnitOccupied ?? null,
    marketRentMonthly: unitInfo?.monthlyMarketRent ?? null,
    currentLeaseTermMonthly:
      unitInfo?.currentLeaseTermsInMonths?.includes(".") ||
      hasOnlySpaces(unitInfo?.currentLeaseTermsInMonths)
        ? null
        : +unitInfo?.currentLeaseTermsInMonths,
    mostRecentLeaseRentMonthly: unitInfo?.inPlaceLeaseRentMonthly ?? null,
    inPlaceLeaseRentMonthly: unitInfo?.inPlaceLeaseRentMonthly ?? null,
  }));
}

// Resolves the display address for a single property, matching
// normalizeSubmitApplicationPropertyData's original inline address
// resolution exactly. Extracted purely to reduce that function's
// cognitive complexity — behavior is unchanged.
function resolvePropertyAddress(
  propertyLocation: any,
  addressInputValue: any,
  propIdx: number,
  dynamicConfig?: any
): any {
  let address = propertyLocation?.address;
  let addressInputText =
    (propIdx || propIdx == 0) && dynamicConfig?.ViteAppKcRealm === "bplsizer"
      ? addressInputValue?.[propIdx]
      : addressInputValue;

  if (
    addressInputValue &&
    Array.isArray(addressInputValue) &&
    dynamicConfig?.ViteAppKcRealm !== "bplsizer"
  ) {
    addressInputText = addressInputValue?.[0];
  }

  if (addressInputText) {
    address = propertyLocation?.address?.includes(addressInputText)
      ? propertyLocation.address
      : addressInputText;
  }

  return (address ?? "")
    ?.replace(propertyLocation?.city, "")
    ?.replace(propertyLocation?.state, "")
    ?.trim();
}

// Computes the propertyUnits count for a property, matching
// normalizeSubmitApplicationPropertyData's original inline nested ternary
// exactly. Extracted purely to reduce that function's cognitive
// complexity — behavior is unchanged.
function computePropertyUnitsCount(numberOfUnits: any): number {
  if (!numberOfUnits) {
    return 1;
  }
  if (hasOnlySpaces(numberOfUnits) || numberOfUnits?.includes(".")) {
    return 1;
  }
  return parseInt(numberOfUnits) ?? 1;
}

export const normalizeSubmitApplicationPropertyData = (
  propertyDetails: ObjectType[],
  loanDetails: ObjectType,
  loanId: string,
  data: any,
  autoCalculatedPropResults: ObjectType[],
  dynamicConfig?: any
) => {
  const properties: any[] = [];
  const { addressInputValue } = data;
  propertyDetails.forEach((propDetail: ObjectType, propIdx: number) => {
    const {
      propertyInformation,
      propertyEconomics,
      propertyLocation,
      unitInformation,
    } = propDetail;
    let closingCosts = 0;

    if (propertyEconomics?.closingCosts) {
      closingCosts = +propertyEconomics?.closingCosts?.split("%")[0];
    }

    const units: ObjectType[] = buildUnitsForProperty(unitInformation, propIdx);

    const address = resolvePropertyAddress(
      propertyLocation,
      addressInputValue,
      propIdx,
      dynamicConfig
    );

    const returnObj = {
      loanId,
      loanPropertyOrder: 1,
      propertyinfo: {
        propertyUnits: computePropertyUnitsCount(
          propertyInformation?.numberOfUnits
        ),
        propertyType: propertyInformation?.propertyType ?? null,
        changeInUseCase:
          yesornToBoolean(propertyInformation?.expansionOrChangeOfUseCase) ??
          null,
        expansion:
          yesornToBoolean(propertyInformation?.expansion) ??
          null,
        isEntitledLand:
          propertyInformation?.entitledAsOfRightDevelopment ?? null,
        isPropertyPurchasedLast2Years:
          propertyInformation?.propertyPurchasedWithinLast2Years ?? null,
        valuationSource: propertyInformation?.valuationSource ?? null,
        isPropertyTypeCondo: propertyInformation?.propertyType === "Condo",
        isPropertyTypeTwoFourUnit:
          propertyInformation?.propertyType === "2-4 Unit",
        squareFootage:
          propertyInformation?.propertySqftFootage?.replaceAll(",", "") ?? null,
        preRehabSquareFootage:
          propertyInformation?.propertySqftFootage?.replaceAll(",", "") ?? null,
        propertyCondition: propertyInformation?.propertyCondition ?? null,
        propertyAcquisitionDate: propertyInformation?.originalDateOfAcquisition ?
          getFormattedDate(propertyInformation?.originalDateOfAcquisition) :
          null,
        isPurchasedWithDebt:
          ["Y", "Yes"].includes(propertyInformation?.propertyWithDebt) ? "Yes" : "No",
        recentPropertyValuationDate:
          getFormattedDate(
            propertyInformation?.recentPropValuationDate ||
              propertyInformation?.mostRecentPropertyValuationDate
          ) ?? null,
        percentOccupied: propertyInformation?.percentOccupied ?? null,
        isPropertyRecentlyRenovated:
          ["Y", "Yes"].includes(propertyInformation?.isPropertyRecentlyRenovated) ? "Yes" : "No",
      },
      propertyLocation: {
        addressLine1: address ?? null,
        addressLine2: "",
        city: propertyLocation?.city ?? null,
        state: propertyLocation?.state ?? null,
        postalCode: propertyLocation?.postalCode ?? null,
        country: propertyLocation?.country ?? null,
        locationValidationStatus: "COMPLETE",
        propertyDesignation: propertyLocation?.propertyDesignation ?? null,
      },
      propertyEconomics: {
        assignmentFees: propertyEconomics?.assignmentFee ?? null,
        originalAsIsAppraisalValue:
          (propertyEconomics?.originalAsIsAppraisalValue ||
            propertyEconomics?.orgAppraisalValue ||
            propertyEconomics?.asIsValue) ??
          null,
        originalAsRepairedAppraisedValue:
          propertyEconomics?.asRepairedValueARV?.toString() ||
          propertyEconomics?.afterRepairValue ||
          null,
        purchasePrice:
          propertyEconomics?.propertyAcquisitionPrice ||
          propertyEconomics?.purchasePrice ||
          null,
        borrowerPaidCosts: propertyEconomics?.borrowerPaidCostToDate ?? null,
        grossCondoSelloutValue:
          propertyEconomics?.grossCondoSelloutValue ?? null,
        costBasis:
          autoCalculatedPropResults[propIdx]?.propertyEconomics?.costBasis ??
          null,
        closingCost: closingCosts,
        additionalEligibleCost:
          propertyEconomics?.additionalEligibleCost ?? null,
        grossPotentialRent:
          propertyEconomics?.estimatedRentPerYearUponCompletion ?? null,
        annualPropertyTaxes: propertyEconomics?.annualPropertyTaxes ?? null,
        appraisalReportedNoiNcf:
          propertyEconomics?.appraisalStabilizedNOI ?? null,
        appraisalReportedNotOrNcf:
          propertyEconomics?.appraisalStabilizedNOI ?? null,
        insurance:
          (propertyEconomics?.insurance ||
            propertyEconomics?.annualInsurance) ??
          null,
        annualHazardInsurance: propertyEconomics?.annualHazardInsurance ?? null,
        annualFloodInsurance: propertyEconomics?.annualFloodInsurance ?? null,
        annualHoaFee: propertyEconomics?.annualHOAFee ?? null,
        thirdPartyValuation:
          (propertyEconomics?.thirdPartyPopertyValue ||
            propertyEconomics?.thirdPartyValuation) ??
          null,
        thirdPartyValuationStatus:
          propertyEconomics?.thirdPartyValuationStatus ?? null,
        marketRentPerMonth:
          propertyEconomics?.grossPotentialRentMonthly ?? null,
        decliningMarkets: propertyEconomics?.decliningMarkets ?? null,
        verifiedCostsAndImprovements:
          propertyEconomics?.verifiedCostsAndImprovements ? propertyEconomics?.verifiedCostsAndImprovements : null,
      },
      propertyUnit: units,
    };
    properties.push(returnObj);
  });

  return properties;
};

function percentageOccupiedFormula(unitInformation: any) {
  const characterization = unitInformation?.rentalCharacterization;
  if (
    [
      LeaseStatusEnum.unOcupiedLongTerm,
      LeaseStatusEnum.unOccupiedShortTerm,
    ]?.includes(characterization)
  ) {
    return "0.0000%";
  }
  if (
    [
      LeaseStatusEnum.occupiedLongTerm,
      LeaseStatusEnum.occupiedShortTerm,
    ]?.includes(characterization)
  ) {
    return "100.0000%";
  }
  return null;
}

// True when the "BRIDGE" property result should include the
// marketData/subMarketData sections, matching normalizeResultLoanData's
// original inline condition exactly. Extracted purely to reduce that
// function's cognitive complexity — behavior is unchanged.
function isMultifamilyOrGroundUpMixedUse(
  loanTypeValue: any,
  loanTypeData: ObjectType
): boolean {
  return (
    (rehabTypes.includes(loanTypeValue) &&
      loanTypeData?.propertyType === "Multifamily/MixedUse") ||
    (["Multifamily/MixedUse"].includes(loanTypeData?.propertyType) &&
      loanTypeData?.loanType === "Ground Up")
  );
}

// Multiplies a value by 100 when it is a finite number, otherwise passes it
// through (defaulting to null), matching the original inline
// "isNumber(...) ? ... * 100 : ... ?? null" ternaries used for every
// marketData/subMarketData field exactly. Extracted purely to reduce
// normalizeResultLoanData's cognitive complexity — behavior is unchanged.
function scalePercentIfNumeric(value: any): any {
  return Number.isFinite(value) ? value * 100 : value ?? null;
}

// Builds either the marketData or subMarketData section of a "BRIDGE"
// property result. The two original inline object literals were identical
// apart from which name field/source object they used, so a single
// parameterized helper reproduces both exactly. Extracted purely to reduce
// normalizeResultLoanData's cognitive complexity — behavior is unchanged.
function buildMarketSectionData(
  existingSection: any,
  sourceSection: any,
  nameField: "marketName" | "subMarketName"
): ObjectType {
  return {
    ...existingSection,
    [nameField]: sourceSection?.[nameField] ?? null,
    capRate: scalePercentIfNumeric(sourceSection?.capRate),
    twelveMonthRentalGrowth: scalePercentIfNumeric(
      sourceSection?.twelveMonthRentalGrowth
    ),
    rentPerSqFt: sourceSection?.rentPerSqft ?? null,
    rentPerUnit: sourceSection?.rentPerUnit ?? null,
    salePriceGrowth: scalePercentIfNumeric(sourceSection?.salePerGrowth),
    salePricePerUnit: sourceSection?.salePerPriceUnit ?? null,
    population: sourceSection?.population ?? null,
    vacancyRate: scalePercentIfNumeric(sourceSection?.vacancyRate),
  };
}

// Computes the loanFeatures.ltmHpa value for a "BRIDGE" property result,
// matching normalizeResultLoanData's original inline ternary exactly.
// Extracted purely to reduce that function's cognitive complexity —
// behavior is unchanged.
function computeLtmHpaPercentage(loanFeaturesSource: any): any {
  if (!(loanFeaturesSource?.ltmHpa || loanFeaturesSource?.medianHomeHpa)) {
    return null;
  }
  return (
    getPercentageValue(
      +loanFeaturesSource?.ltmHpa || +loanFeaturesSource?.medianHomeHpa,
      true,
      4
    ) ?? null
  );
}

export const normalizeResultLoanData = (
  loanData: any,
  loanResultData: any,
  loanType: string,
  propertiesResults: ObjectType,
  tempPropertiesResult: ObjectType[],
  loanTypeData: ObjectType
) => {
  const propertyResult: ObjectType[] = [];

  const propertyKeys: any = [];
  Object.keys(propertiesResults ?? {})?.forEach((key: string) => {
    propertyKeys.push(propertiesResults[key]);
  });

  let tempLoanDetails: any = {};

  let tempLoanTypeValue: any = loanType; // for 2.0
  if (loanType === "BRIDGE") {
    tempLoanTypeValue = loanTypeData?.loanType;
  }

  const loanCharacterisation =
    loanResultData?.loanCharacterization ||
    loanResultData?.loanCharacterisation ||
    loanResultData?.loanCharacterisations;

    switch (loanType) {
    case "30Year":
      tempLoanDetails = {
        ["loanEconomics"]: {
          ...tempLoanDetails["loanEconomics"],
          loanLevelOccupancyPercentage: isFalsyButNotZero(
            loanResultData?.loanEconomics?.loanLevelOccupancy
          )
            ? loanResultData?.loanEconomics?.loanLevelOccupancy * 100
            : null,
          rehabLoanAmount:
            +loanData?.loanEconomics?.hardCosts +
            +loanData?.loanEconomics?.softCosts,
          dscr: loanResultData?.loanEconomics?.dscr,
          requestedAsIsLTV: getPercentageValue(
            loanResultData?.loanFeatures?.originalAsIsLtv,
            true
          ),
          grossArmMargin: getPercentageValue(
            loanResultData?.loanPricing?.grossArmMargin,
            true
          ),
          initialPeriodicCap: getPercentageValue(
            loanResultData?.loanPricing?.initialPeriodicCap,
            true
          ),
          subsequentPeriodicCap: getPercentageValue(
            loanResultData?.loanPricing?.subsequentPeriodicCap,
            true
          ),
          lifeTimeCap: getPercentageValue(
            loanResultData?.loanPricing?.lifeTimeCap,
            true
          ),
          lifeTimeFloor: getPercentageValue(
            loanResultData?.loanPricing?.lifeTimeFloor,
            true
          ),
        },
        ["loanFeatures"]: {
          originalAsIsLtv: getPercentageValue(
            loanResultData?.loanFeatures?.originalAsIsLtv,
            true
          ),
          originalLtcLtp: getPercentageValue(
            loanResultData?.loanFeatures?.originalLtc,
            true
          ),
          originalLtc: getPercentageValue(
            loanResultData?.loanFeatures?.originalLtc,
            true
          ),
        },
        ["loanCharacterisation"]: {
          toorakFICObucket: loanCharacterisation?.toorakFicoBucket ?? null,
          toorakLoanType: loanCharacterisation?.toorakLoanType ?? null,
          finalLoanCharacterisation:
            loanCharacterisation?.finalLoanCharacterization ?? null,
        },
        ["loanInformation"]: {
          loanPurpose: loanCharacterisation?.finalLoanCharacterization,
        },
      };

      Object.keys(tempPropertiesResult ?? [])?.forEach((key: string, idx: number) => {
        const property = tempPropertiesResult[0]  ? tempPropertiesResult[idx]  : tempPropertiesResult[idx + 1];
        propertyResult.push({
          loanFeatures: {
            ...property?.loanFeatures,
            eSRIPopulationDensity:
              propertyKeys[idx]?.loanFeatures?.esriPopulationDensity,
            zillowCBSA: propertyKeys[idx]?.loanFeatures?.zillowCbsa,
            adjustedAsIsValue:
              propertyKeys[idx]?.loanFeatures?.adjustedAsIsValue,
          },
          propertyInformation: {
            ...property?.propertyInformation,
            precentOccupied: loanResultData?.loanEconomics?.loanLevelOccupancy
              ? loanResultData.loanEconomics.loanLevelOccupancy * 100
              : null,
          },
          propertyEconomics: {
            costBasisCalculated: (propertyKeys[idx]?.loanEconomics?.costBasisCalculated || (+tempPropertiesResult[idx]?.propertyEconomics?.propertyAcquisitionPrice + +tempPropertiesResult[idx]?.propertyEconomics?.verifiedCostsAndImprovements)) ?? null,
          },
          unitInformation: property?.unitInformation ? property?.unitInformation?.map(
            (unitInfo: ObjectType, unitIdx: number) => {
              return {
                ...unitInfo,
                monthlyUnitRent:
                  propertyKeys[idx]?.[`unit${unitIdx}`]?.monthlyGrossRent,
                percentageOccupied:
                  propertyKeys[idx]?.[`unit${unitIdx}`]?.percentOccupied,
                percentUnitOccupied: percentageOccupiedFormula(unitInfo),
                monthlyGrossRent: property?.[`unit${unitIdx + 1}`]?.monthlyGrossRent,
              };
            }
          ) : extractUnitsResultData(property ?? {}),
        });
      });
      break;
    case "BRIDGE":
      tempLoanDetails = {
        ...tempLoanDetails,
        ["borrowerInformation"]: {
          ...tempLoanDetails["borrowerInformation"],
          experiencedNew: loanCharacterisation?.customerType ?? null,
        },
        ["loanEconomics"]: {
          ...tempLoanDetails["loanEconomics"],
          rehabLoanAmount:
            +loanData?.loanEconomics?.hardCosts +
              +loanData?.loanEconomics?.softCosts || null,
          totalRehabBudget:
            +loanData?.loanEconomics?.hardCosts +
              (+loanData?.loanEconomics?.softCosts ||
                +loanData?.loanEconomics?.softCostsCap15Rehab) || null,
        },
        ["loanInformation"]: {
          ...tempLoanDetails["loanInformation"],
          requiredInterestReserve:
            loanCharacterisation?.requiredInterestReservesAmount ?? null,
          requiredInterestReserveMonths:
            loanCharacterisation?.requiredInterestReservesMonth ?? null,
        },
        ["loanFeatures"]: {
          ...tempLoanDetails["loanFeatures"],
          originalAsIsLtv: getPercentageValue(
            loanResultData?.loanFeatures?.originalAsIsLtv,
            true
          ),
          originalAsRepairedLTV: getPercentageValue(
            loanResultData?.loanFeatures?.originalAsIsRepairedLtv,
            true
          ),
          originalLtc: getPercentageValue(
            loanResultData?.loanFeatures?.originalLtc,
            true
          ),
          asIsLTV: getPercentageValue(
            loanResultData?.loanFeatures?.originalAsIsLtv,
            true
          ),
          ltcAsIs: getPercentageValue(
            loanResultData?.loanFeatures?.originalLtc,
            true
          ),
          aRVLTVAsCompleted: getPercentageValue(
            loanResultData?.loanFeatures?.arvLtvAsCompleted,
            true
          ),
          blendedLTCAsCompleted: getPercentageValue(
            loanResultData?.loanFeatures?.blendedLtcAsCompleted,
            true
          ),
          blendedLTCwithFundedIRAsCompleted: getPercentageValue(
            loanResultData?.loanFeatures?.blendedLTCWithFundedIRAsCompleted,
            true
          ),
        },
        ["loanCharacterisation"]: {
          ...tempLoanDetails["loanCharacterisation"],
          customerTypeExperiencedNew:
            loanCharacterisation?.customerType ?? null,
          purchaseOrRefinance:
            loanCharacterisation?.purchaseOrRefinance ?? null,
          rehabType: loanCharacterisation?.rehabType ?? null,
          stabilizedFlag: loanCharacterisation?.stabilizedFlag ?? null,
          loanCharacterisation:
            loanCharacterisation?.loanCharacterisation ?? null,
          tier: loanCharacterisation?.tier ?? null,
          loanPurpose: loanCharacterisation?.loanPurpose ?? null,
          indexOfMostRestrictiveProperty:
            loanCharacterisation?.indexOfMostRestrictiveProperty ?? null,
        },
      };

      if (tempLoanTypeValue === "Ground Up") {
        tempLoanDetails = {
          ...tempLoanDetails,
          ["loanEconomics"]: {
            ...tempLoanDetails["loanEconomics"],
            totalBudget:
              +loanData?.loanEconomics?.hardCosts +
                +loanData?.loanEconomics?.softCosts ||
              +loanData?.loanEconomics?.hardCosts +
                +loanData?.loanEconomics?.softCostsCap15Rehab ||
              null,
          },
        };
      }
      Object.keys(tempPropertiesResult)?.forEach((key: string, idx: number) => {
        const property = tempPropertiesResult[idx];
        const propObj: ObjectType = {
          loanFeatures: {
            ...property?.loanFeatures,
            eSRIPopulationDensity:
              propertyKeys[idx]?.loanFeatures?.esriPopulationDensity,
            cbsa: propertyKeys[idx]?.loanFeatures?.cbsa ?? null,
            medianValue:
              (propertyKeys[idx]?.loanFeatures?.medianValue ||
                propertyKeys[idx]?.loanFeatures?.medianHomePriceValue) ??
              null,
            ltmHpa: computeLtmHpaPercentage(propertyKeys[idx]?.loanFeatures),
            daysOnMarket: propertyKeys[idx]?.loanFeatures?.daysOnMarket ?? null,
          },
          propertyEconomics: {
            ...property.propertyEconomics,
            costBasis:
              getCostBasisValue(property?.propertyEconomics, loanTypeData) ??
              null,
          },
        };

        if (isMultifamilyOrGroundUpMixedUse(tempLoanTypeValue, loanTypeData)) {
          propObj["marketData"] = buildMarketSectionData(
            property["marketData"],
            propertyKeys[idx]?.marketData,
            "marketName"
          );
          propObj["subMarketData"] = buildMarketSectionData(
            property["subMarketData"],
            propertyKeys[idx]?.subMarketData,
            "subMarketName"
          );
        }
        propertyResult.push(propObj);
      });
      break;
    default:
      break;
  }
  return { tempLoanDetails, propertyResult };
};

export function getCookie(name: string) {
  return Cookies.get(name) === undefined ? null : Cookies.get(name);
}

export function setCookie(name: string, value: any, domain?: string) {
  const date = new Date();
  date.setTime(date.getTime() + 1 * 24 * 60 * 60 * 1000);
  const secureFlag: any = window.location.hostname !== "localhost";
  let options: { expires?: Date; secure?: boolean; domain?: string } = {
    expires: date,
    secure: secureFlag,
  };
  options = domain ? { ...options, domain } : options;
  Cookies.set(name, value, options);
}

export function removeAllCookie() {
  const cookies = Cookies.get();
  for (const cookieName in cookies) {
    if (Object.prototype.hasOwnProperty.call(cookies, cookieName)) {
      Cookies.remove(cookieName);
    }
  }
}

// Formatting methods a template's ${...} placeholder is allowed to call on
// a resolved value. Deliberately small — anything not listed here (and any
// non-method-call expression shape at all) falls through to `undefined`
// rather than being executed.
const SAFE_TEMPLATE_METHODS: Record<string, (value: any, args: any[]) => any> = {
  toFixed: (value: any, args: any[]) => value?.toFixed?.(...args),
  toString: (value: any, args: any[]) => value?.toString?.(...args),
  toLocaleString: (value: any, args: any[]) => value?.toLocaleString?.(...args),
  toUpperCase: (value: any) => value?.toUpperCase?.(),
  toLowerCase: (value: any) => value?.toLowerCase?.(),
  trim: (value: any) => value?.trim?.(),
};

// Rewrites bracket-notation property access (e.g. "items['bar']", "items[0]")
// to dot notation ("items.bar", "items.0") via a manual left-to-right scan
// instead of the regex /\[(['"]?)([^\]]+)\1\]/g. That pattern's inner class
// ([^\]]+) overlaps the quote characters the optional group/backreference
// match, and with the "g" flag an unmatched "[" retries that ambiguity at
// every position in the string — the same super-linear backtracking shape
// flagged (and removed) elsewhere in this file. A single scan has no
// backtracking to exploit.
function normalizeBracketAccess(expression: string): string {
  let result = "";
  let i = 0;
  while (i < expression.length) {
    if (expression[i] !== "[") {
      result += expression[i];
      i++;
      continue;
    }
    const close = expression.indexOf("]", i + 1);
    if (close === -1) {
      result += expression.slice(i);
      break;
    }
    let inner = expression.slice(i + 1, close);
    const first = inner[0];
    const last = inner.at(-1);
    if (inner.length >= 2 && (first === "'" || first === '"') && first === last) {
      inner = inner.slice(1, -1);
    }
    result += `.${inner}`;
    i = close + 1;
  }
  return result;
}

// Resolves a dotted/bracketed property-access path (e.g.
// "request.loanResult.someField" or "request.items[0].value") against the
// given scope object — the same shape `eval` would read via closure — but
// via plain property lookups, so a template can never run arbitrary code.
// Missing segments resolve to undefined, matching normal JS property access
// on a missing key rather than throwing.
function resolveTemplatePath(expression: string, scope: Record<string, any>): any {
  const normalized = normalizeBracketAccess(expression.trim());
  const [rootKey, ...rest] = normalized.split(".").filter(Boolean);
  let current: any = rootKey ? scope[rootKey] : undefined;
  for (const key of rest) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
}

// Parses a "path.methodName(args)" expression shape via plain string
// slicing instead of the regex /^([\w.[\]'"]+)\.(\w+)\(([^()]*)\)$/ — that
// pattern's path-segment class ([\w.[\]'"]+) includes "." and so overlaps
// the literal "." right after it, the same backtracking-ambiguity shape
// flagged (and removed) elsewhere in this codebase for the email regexes.
// Manual slicing has no backtracking to exploit. Returns null for anything
// that isn't a single, unnested call at the end of the string, matching the
// old regex's behavior exactly.
function parseTemplateMethodCall(
  trimmed: string
): { pathPart: string; methodName: string; argsPart: string } | null {
  if (!trimmed.endsWith(")")) return null;

  const openParen = trimmed.indexOf("(");
  if (openParen === -1) return null;

  const beforeCall = trimmed.slice(0, openParen);
  const argsPart = trimmed.slice(openParen + 1, -1);
  if (argsPart.includes("(") || argsPart.includes(")")) return null;

  const lastDot = beforeCall.lastIndexOf(".");
  if (lastDot === -1) return null;

  const pathPart = beforeCall.slice(0, lastDot);
  const methodName = beforeCall.slice(lastDot + 1);
  if (!pathPart || !/^\w+$/.test(methodName) || !/^[\w.[\]'"]+$/.test(pathPart)) {
    return null;
  }

  return { pathPart, methodName, argsPart };
}

// Evaluates a single ${...} placeholder body: a property-access path,
// optionally followed by one whitelisted formatting call (e.g.
// ".toFixed(2)"). This is the only expression shape these templates are
// known to use — anything else (arithmetic, ternaries, arbitrary calls)
// intentionally resolves to undefined instead of being run.
function evaluateTemplateExpression(expression: string, scope: Record<string, any>): any {
  const trimmed = expression.trim();
  const callMatch = parseTemplateMethodCall(trimmed);
  if (!callMatch) {
    return resolveTemplatePath(trimmed, scope);
  }
  const { pathPart, methodName, argsPart } = callMatch;
  const method = SAFE_TEMPLATE_METHODS[methodName];
  if (!method) return undefined;
  const value = resolveTemplatePath(pathPart, scope);
  const args = argsPart
    .split(",")
    .map((arg) => arg.trim())
    .filter(Boolean)
    .map((arg) => (Number.isNaN(Number(arg)) ? arg.replaceAll(/^['"]|['"]$/g, "") : Number(arg)));
  try {
    return method(value, args);
  } catch {
    return undefined;
  }
}

// Safely renders a backend-provided template-literal string (e.g.
// "`Failed because ${request.loanResult.x} exceeds limit`") against a fixed
// scope, without eval()'s arbitrary code execution.
function renderTemplateLiteral(template: string, scope: Record<string, any>): string {
  const body = template.slice(1, -1); // strip the surrounding backticks
  return body.replaceAll(/\$\{([^}]*)\}/g, (_match, expr) => {
    const value = evaluateTemplateExpression(expr, scope);
    return value === undefined || value === null ? "undefined" : String(value);
  });
}

// Checked with plain string methods rather than /^`[\s\S]*`$/ — that
// pattern's [\s\S]* class includes the backtick it's anchored against,
// which is the same overlapping-class-then-literal shape flagged elsewhere
// in this file. startsWith/endsWith has no backtracking to exploit.
const isTemplateLiteralString = (value: string) =>
  value.length >= 2 && value.startsWith("`") && value.endsWith("`");

export function evaluation(
  templateObject: string,
  request: ObjectType,
  templateLabels: any
) {
  // NOTE: Do not remove console statement as vite build is removing unused variable
  console.log("request", request?.loan?.loanStage);
  if (!templateObject) {
    return templateLabels?.genericTemplateMessage;
  }
  // Every real template is a backtick-delimited literal — anything else
  // (eval would have thrown trying to run it as a statement) falls back to
  // the generic message immediately, same as the try/catch used to.
  if (!isTemplateLiteralString(templateObject)) {
    return templateLabels?.genericTemplateMessage;
  }

  const scope = { request, templateLabels };
  let firstEvaluation: string;
  try {
    firstEvaluation = renderTemplateLiteral(templateObject, scope);
  } catch {
    return templateLabels?.genericTemplateMessage;
  }
  if (firstEvaluation.includes("undefined")) {
    return templateLabels?.genericTemplateMessage;
  }

  // Some templates resolve to *another* template-literal string (one level
  // of indirection) — render that too, mirroring the original's second pass.
  if (isTemplateLiteralString(firstEvaluation)) {
    try {
      let secondEvaluation = renderTemplateLiteral(firstEvaluation, scope);
      secondEvaluation = secondEvaluation.replaceAll('"undefined"', "").replaceAll("undefined", "");
      return secondEvaluation;
    } catch {
      return firstEvaluation;
    }
  }
  return firstEvaluation;
}

// Template label values (testNames.* entries) come back JSON-stringified
// from the template API (e.g. '"Max LTV Test"') — this un-quotes them
// without eval()'s arbitrary code execution. Returns undefined (matching
// eval's behavior for non-string/missing input, rather than throwing) so
// callers' `?? fallback` pattern keeps working when a label is absent or
// malformed.
export function parseTemplateLabel(value: any): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

export const getOverAllResults = (failingTests: any) => {
  let result = "FAIL";

  const failingTest = failingTests.filter((test: any) => {
    return test.overAllResults === "FAIL";
  });
  failingTest.length > 0 ? (result = "FAIL") : (result = "PASS");
  return result;
};

export const evaluationTestFieldTypes: { [key: string]: any } = {
  guidelineTest: {
    profitabilityTest: {
      isTest: true,
      fieldType: "string",
      readableName: "Profitability Test",
    },
    rentYieldTest: {
      isTest: true,
      fieldType: "string",
      readableName: "Rent Yield Test",
    },
  },
  loanEconomics: {
    annualizedIOPayments: {
      fieldType: "currency",
      readableName: "Annualized IO Payments",
    },
    dscrCalculation: {
      fieldType: "currency", // changed
      readableName: "DSCR Calculation",
    },
    profitabilityCalculation: {
      fieldType: "percentage",
      readableName: "Profitability Calculation",
    },
    profitabilityTest: {
      isTest: true,
      fieldType: "string",
      readableName: "Profitability Test",
    },
    rentYieldCalculation: {
      fieldType: "currency", // changed
      readableName: "Rent Yield Calculation",
    },
    rentYieldTest: {
      isTest: true,
      fieldType: "string",
      readableName: "Rent Yield Test",
    },
    dscrRequirementTest: {
      isTest: true,
      fieldType: "string",
      readableName: "DSCR Requirement Test",
    },
    monthlyInterest: {
      fieldType: "currency",
    },
    monthlyPrincipalDuringAmortization: {
      fieldType: "currency",
    },
    monthlyQualifyingPI: {
      fieldType: "currency",
    },
    monthlyQualifyingPayment: {
      fieldType: "currency",
    },
    // prePaymentPenaltyMonths not in sheet for dscr
  },
  guideLineLimit: {
    maxTerm13to18Months: {
      fieldType: "string",
      isTest: true,
      readableName: "Maximum Term 13 to 18 Months",
    },
    minPerUnit100kMSA: {
      fieldType: "string",
      isTest: true,
      readableName: "Minimum Per Unit (100k MSA)",
    },
    minPerUnit150kMSA: {
      fieldType: "string",
      isTest: true,
      readableName: "Minimum Per Unit (150k MSA)",
    },
    budget150percentageAsIsPurchasePrice: {
      fieldType: "string",
      isTest: true,
      readableName: "Budget 150% Age As Is Purchase Price",
    },
    interestReserves: {
      fieldType: "string",
      isTest: true,
      readableName: "Interest Reserves",
    },
    maxLeverageArvLtv: {
      fieldType: "string",
      isTest: true,
      readableName: "Maximum Leverage ArvLtv",
    },
    maxLeverageAsIsLtv: {
      fieldType: "string",
      isTest: true,
      readableName: "Maximum Leverage As Is Ltv",
    },
    maxLeverageLtc: {
      fieldType: "string",
      isTest: true,
      readableName: "Maximum Leverage Ltc",
    },
    maxLoanSize: {
      fieldType: "string",
      isTest: true,
      readableName: "Maximum Loan Size",
    },
    maxTerm0To13Months: {
      fieldType: "string",
      isTest: true,
      readableName: "Maximum Term 0 To 13 Months",
    },
    maxTerm18To24Months: {
      fieldType: "string",
      isTest: true,
      readableName: "Maximum Term > 18 Months",
    },
    minLoanSize: {
      fieldType: "string",
      isTest: true,
      readableName: "Minimum Loan Size",
    },
    minMedianFico: {
      fieldType: "string",
      isTest: true,
      readableName: "Minimum Median Fico",
    },
    minPerUnit175kMSA: {
      fieldType: "string",
      isTest: true,
      readableName: "Minimum Per Unit 175kMSA",
    },
    minTerm12Months: {
      fieldType: "string",
      isTest: true,
      readableName: "Minimum Term 12Months",
    },
    msapropertyLocationHighValue150KPart1: {
      fieldType: "string",
      readableName: "MSA Property Location High Value 150K Part1",
    },
    msapropertyLocationHighValue150KPart2: {
      isTest: true,
      fieldType: "string",
      readableName: "MSA Property Location High Value 150K Part2",
    },
    msapropertyLocationHighValue175KPart1: {
      fieldType: "string",
      readableName: "MSA Property Location High Value 175K Part1",
    },
    msapropertyLocationHighValue175KPart2: {
      isTest: true,
      fieldType: "string",
      readableName: "MSA Property Location High Value 175K Part2",
    },
    msapropertyLocationHighValuePart1: {
      fieldType: "string",
      readableName: "MSA Property Location High Value Part1",
    },
    msapropertyLocationHighValuePart2: {
      isTest: true,
      fieldType: "string",
      readableName: "MSA Property Location High Value Part2",
    },
    nonStabilizedPerUnit: {
      isTest: true,
      fieldType: "string",
      readableName: "Non Stabilized Per Unit",
    },
    sfrNotStabilisedMinValue: {
      isTest: true,
      fieldType: "string",
      readableName: "Sfr Not Stabilised Min Value",
    },
    stabilizedPerUnit: {
      isTest: true,
      fieldType: "string",
      readableName: "Stabilized Per Unit",
    },
  },
  logicTest: {
    agedCreditReport: {
      isTest: true,
      fieldType: "string",
      readableName: "Aged Credit Report",
    },
    asIsOrCostGreaterThanArv: {
      isTest: true,
      fieldType: "string",
      readableName: "As Is Or Cost Greater Than Arv",
    },
    purchasegreaterThan120PercentOfAsIs: {
      isTest: true,
      fieldType: "string",
      readableName: "Purchase Greater Than 120% Of As Is",
    },
    recourseTest: {
      isTest: true,
      fieldType: "string",
      readableName: "Recourse Test",
    },
  },
  multiFamilyMixedUse: {
    loanLimitforApproval: {
      fieldType: "string",
      readableName: "Loan Limit for Approval",
    },
    actualDebtYield: {
      fieldType: "percentage",
      readableName: "Actual Debt Yield",
    },
    appraisalNoiorNof: {
      fieldType: "currency", // changed
      readableName: "Appraisal Noi or Nof",
    },
    debtYeildTest: {
      isTest: true,
      fieldType: "string",
      readableName: "Debt Yeild Test",
    },
    minDebtYeildRequirement: {
      fieldType: "string", // number field
      readableName: "Min Debt Yeild Requirement",
    },
    minLiquidityRequirement: {
      fieldType: "string",
      readableName: "Minimum Liquidity Requirement",
    },
    operatingExpenseandVacancy: {
      fieldType: "string", // number field
      readableName: "Operating Expense and Vacancy",
    },
    toorakNoiorNof: {
      fieldType: "currency",
      readableName: "Toorak Noi or Nof",
    },
    underwrittenNoiorNof: {
      fieldType: "currency",
      readableName: "Underwritten Noi or Nof",
    },
    valuePerUnit: {
      fieldType: "currency",
      readableName: "Value Per Unit",
    },
  },
  bridgeRentalPropertyTest: {
    agedPropertyValuation: {
      isTest: true,
      fieldType: "string",
      readableName: "Aged Property Valuation",
    },
    desktopRequirement: {
      isTest: true,
      fieldType: "string",
      readableName: "Desktop Requirement",
    },
    feasibilityReportRequired: {
      fieldType: "string",
      readableName: "Feasibility Report Required",
    },
    homePriceDecline: {
      isTest: true,
      fieldType: "string",
      readableName: "Home Price Decline",
    },
    minimumPropertyValueTest: {
      isTest: true,
      fieldType: "string",
      readableName: "Minimum Property Value Test",
    },
    minSquareFootage: {
      isTest: true,
      fieldType: "string",
      readableName: "Minimum Square Footage",
    },
    propertyLevelTest: {
      isTest: true,
      fieldType: "string",
      readableName: "Property Level Test",
    },
    significantExpansion: {
      fieldType: "string",
      readableName: "Significant Expansion",
    },
    loanStructure: {
      isTest: true,
      fieldType: "string",
      readableName: "Loan Structure",
    },
  },
  maxLeverageTest: {
    maxLTCOne: {
      fieldType: "percentage",
      readableName: "Maximum LTC One",
    },
    maxLTVARV: {
      fieldType: "percentage",
      readableName: "Maximum LTV ARV",
    },
    maxLTVAsIs: {
      fieldType: "percentage",
      readableName: "Maximum LTV As Is",
    },
  },
};

export const guidlineTestsMap: any = {
  medianHomeValueMultiplier: "Median Home Value Multiplier",
  noEntitlements: "No Entitlements",
  experienceCompletedGUCProjects: "Experience",
  minValueForCondoTh100KUnit: "Min Value For 2-4 Unit Condo/TH $100,000/Unit",
  minValueForSF150KHome: "Min Value For SF $150,000/Home or 2-4 Houses /Home",
};

export function getFailedTestName(evaluationResults: any, testType: string) {
  const failedTestNames: any[] = [];
  const validKeys =
    evaluationResults &&
    Object.keys(evaluationResults).filter(
      (key) =>
        typeof evaluationResults[key] === "object" && evaluationResults[key]
    );

  if (validKeys) {
    validKeys.forEach((key: any) => {
      const object = evaluationResults[key];
      Object.entries(object).forEach((entry) => {
        if (key === "warehouseLine") {
          failedTestNames.push(
            `loan.${testType}.${key}.${entry[0]}.message.FAIL`
          );
        }

        if (
          typeof entry[1] === "string" &&
          (entry[1].toLowerCase() === "fail" ||
            entry[1].toLowerCase() === "waived") &&
          entry[0].toLowerCase() !== "sectioneligibility" &&
          entry[0].toLowerCase() !== "propertyleveltest"
        ) {
          failedTestNames.push(`${testType}.${key}.${entry[0]}.message.FAIL`);
        }
        failedTestNames.push(`testNames.${entry[0]}`);
      });
      failedTestNames.push(`testNames.${key}`);
    });
  }
  return failedTestNames;
}

export const isNullOrUndefined = (value: any) => {
  if (value === null || value === undefined) {
    return true;
  }
  return false;
};

export function isEmptyValue(inputValue: any) {
  let value = inputValue;
  if (typeof value === "string") value = value.trim();
  return value === "" || isNullOrUndefined(value);
}

export function getNumFromCommaSeparatedNum(value: any) {
  return typeof value === "string"
    ? Number(value.replace(/,/g, ""))
    : Number(value);
}

export const currencyToNumber = (value: string) => {
  try {
    return value?.replace("$", "")?.replace(/,/g, "");
  } catch (error) {
    console.error(error);
    return value;
  }
};

// Multiplies text (treated as a fraction) by 100 and formats it, matching
// sanitizePercentage's original inline "multiplyBy100" branch exactly.
function multiplyPercentageBy100(text: any, deci: number): string {
  const percentage = typeof text === "number" ? text : Number(text);
  const textToChange = isNaN(percentage)
    ? `${text}`
    : `${(percentage * 100).toFixed(deci)}`;
  return textToChange.replace(/\.$/, "").replace(/%/, "");
}

// Fixes the decimal places of an already-percentage-shaped value, matching
// sanitizePercentage's original inline "else" branch exactly.
function fixDecimalPlaces(result: any, deci: number): any {
  const temp = Number(result);
  return isNaN(temp) ? result : temp.toFixed(deci);
}

export function sanitizePercentage(
  text: any,
  multiplyBy100?: boolean,
  deci: number = 4
) {
  if (text || text === 0) {
    let result = text;
    if (typeof result === "string" && result?.includes("%"))
      result = result.replace("%", "");
    result = multiplyBy100
      ? multiplyPercentageBy100(text, deci)
      : fixDecimalPlaces(result, deci);
    return `${result}%`;
  }
  return text;
}

export function isValidPercentage(text: string, blockNegative?: boolean) {
  let textToCheck = text;
  if (text && text.search("-") >= 0) {
    if (blockNegative) {
      return false;
    }
    textToCheck = text.replace("-", "");
  }
  return /^\d+(\.\d+)?%{0,1}$/.test(textToCheck);
}

export const isValidCurrency = (text: string) => {
  return /^\${0,1}[0-9]\d*(((,\d{3}){1})*(\.\d{1,2})?)$/.test(text);
};

export function MMMDDYYYY(date: any) {
  try {
    if (!date) return "";
    let nDate = date;
    if (typeof nDate === "number") {
      nDate = moment(new Date(nDate));
    } else if (typeof nDate === "string") {
      nDate = String(nDate).split("T")[0];
    }
    return moment.utc(nDate).format("MMM DD, YYYY");
  } catch (err) {
    return "";
  }
}

// Matches formatValueByType's original "percentageToFloat" case body
// exactly. Extracted purely to reduce that function's cognitive complexity —
// behavior is unchanged.
function formatPercentageToFloat(value: any, fieldType?: string): any {
  try {
    const temp = value.replace("%", "");
    if (isNaN(temp)) {
      return temp;
    }
    return Number.parseFloat(temp) / 100;
  } catch (e) {
    console.error(e, value, fieldType);
    return value;
  }
}

// Matches formatValueByType's original "currency"/"currencyWithMinus" case
// body exactly. Extracted purely to reduce that function's cognitive
// complexity — behavior is unchanged.
function formatCurrencyValue(value: any): any {
  const formattedValue = sanitizeCurrency(value);
  return isValidCurrency(formattedValue)
    ? Number(value) < 0
      ? formattedValue.replace("$", "$ -")
      : formattedValue
    : value;
}

// Matches formatValueByType's original "number" case body exactly.
// Extracted purely to reduce that function's cognitive complexity —
// behavior is unchanged.
function formatNumberValue(value: any): any {
  if (isEmptyValue(value)) {
    return value;
  }
  const formattedValue = Number(value);
  if (isNaN(formattedValue)) {
    return value;
  }
  return formattedValue;
}

export function formatValueByType(value: any, fieldType?: string) {
  try {
    switch (fieldType) {
      case "string":
        return isEmptyValue(value) ? value : `${value}`;
      case "percentage":
      case "percentageWithMinus": {
        const formattedValue = sanitizePercentage(value, true);
        return isValidPercentage(formattedValue) ? formattedValue : value;
      }
      case "percentageTwoDecimal": {
        const formattedValue = sanitizePercentage(value, true, 2);
        return isValidPercentage(formattedValue) ? formattedValue : value;
      }
      case "percentageToFloat": {
        return formatPercentageToFloat(value, fieldType);
      }
      case "percentageWithout100x": {
        const formattedValue = sanitizePercentage(value, false);
        return isValidPercentage(formattedValue) ? formattedValue : value;
      }
      case "currency":
      case "currencyWithMinus": {
        return formatCurrencyValue(value);
      }
      case "decimal": {
        const formattedValue = sanitizeCurrency(value);
        return formattedValue !== "$NaN"
          ? formattedValue.replace(/,/g, "").replace("$", "")
          : value;
      }
      case "number": {
        return formatNumberValue(value);
      }
      case "MMMDDYYYYDate": {
        return MMMDDYYYY(value);
      }
      case "decimalFixedto3": {
        return Number.parseFloat(value).toFixed(3);
      }
      case "decimalFixedto4": {
        return Number.parseFloat(value).toFixed(4);
      }
      case "checkbox": {
        return value?.toString() ?? "true";
      }
      default:
        return value;
    }
  } catch (error) {
    console.error(error, value);
    return value;
  }
}

export const totalRehabBudgeFormula = (
  data: any,
  dontDoConversion?: boolean
) => {
  try {
    const { loanDetails } = data;
    const hardCost = currencyToNumber(
      loanDetails?.fesLoanEconomics?.hardCost ?? ""
    );
    const softCost = currencyToNumber(
      loanDetails?.fesLoanEconomics?.softCost ?? ""
    );
    if (!isEmptyValue(hardCost) && !isEmptyValue(softCost)) {
      const value = Number(hardCost) + Number(softCost);
      return dontDoConversion
        ? value
        : formatValueByType(value.toString(), "currency");
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

export function yesornToBoolean(value: any) {
  if (value === "Yes" || value === "Y") {
    return true;
  }
  if (value === "No" || value === "N") {
    return false;
  }
  return null;
}

// The following helpers each isolate one section of the loan request
// payload built by getLoanRequestObject. Extracted purely to reduce that
// function's cognitive complexity — behavior is unchanged from the original
// inline code.

function getCrossCollaterlizationValue(crossCollaterlization: any) {
  // Boolean value only for loan request  body
  let selectedCrossValue;
  if (crossCollaterlization === "Y") {
    selectedCrossValue = true;
  } else if (crossCollaterlization === "N") {
    selectedCrossValue = false;
  }
  return selectedCrossValue;
}

function buildBorrowerInfoEntry(borrowerInformation: any) {
  const {
    propertiesCompletedWithinLast36Months,
    borrowerGuarantor,
    borrowerType,
    creditScoreActual,
    borrowerGUCExperience,
    heavyRehabExperience,
    inState,
  } = borrowerInformation;

  const partyType = borrowerType === "Entity" ? "account" : "person";
  const accountType = borrowerType === "Entity" ? "BORROWER" : "person";

  return {
    loanUserType: "Borrower",
    loanUserSequence: "1",
    isPrimary: borrowerType !== "Entity",
    originalCreditScoreMedian: isEmptyValue(creditScoreActual)
      ? null
      : getNumFromCommaSeparatedNum(creditScoreActual).toString(),
    originalCreditScoreReportDate: null,
    experience: isEmptyValue(propertiesCompletedWithinLast36Months)
      ? null
      : getNumFromCommaSeparatedNum(propertiesCompletedWithinLast36Months),
    inState: isEmptyValue(inState) ? null : inState,
    borrowerGUCExperience: isEmptyValue(borrowerGUCExperience)
      ? null
      : getNumFromCommaSeparatedNum(borrowerGUCExperience),
    heavyRehabExperience: isEmptyValue(heavyRehabExperience)
      ? null
      : getNumFromCommaSeparatedNum(heavyRehabExperience),
    ownershipOfEntity: null,
    customer: {
      accountName: "",
      accountType,
      addressList: [],
      contactList: [],
      dateOfBirth: null,
      firstName: "",
      middleName: null,
      lastName: null,
      partyType,
      foreignNationalString: borrowerGuarantor,
    },
  };
}

function buildLoanEconomicsPayload(
  loanEconomics: any,
  createLoanParams: any,
  rehabBudgetValue: any,
  isRateTypeFixed: boolean
) {
  const {
    rehabBudget,
    existDebt,
    softCost,
    hardCost,
    originalLoanAmount,
    totalOriginationAndDiscountPoints,
    interestRate,
    prepaymentPenalty,
    prepayPenaltyType,
    rateType,
    interestOnlyPeriod,
    interestReserve,
    includeOutOfPocketBudgetInARLTV,
    armType,
    armIndex,
    grossArmMargin,
    initialRateAdjustmentDate,
    initialPayAdjustmentDate,
    initialPeriodicCap,
    subsequentPeriodicCap,
    lifeTimeCap,
    lifeTimeFloor,
  } = loanEconomics;

  return {
    totalBudgetAmount: rehabBudgetValue
      ? rehabBudgetValue?.replace("$", "")
      : rehabBudget?.replace("$", "") || 0,
    originalLoanAmount: originalLoanAmount?.replace("$", "") || null,
    rehabAmount:
      totalRehabBudgeFormula({ loanDetails: createLoanParams })?.replace(
        "$",
        ""
      ) || null,
    existDebt: existDebt?.replace("$", "") || null,
    softCost: softCost?.replace("$", "") || null,
    hardCost: hardCost?.replace("$", "") || null,
    // originalLoanAmount:borrowerRquested,
    interestRateAsOfCutOffDate: interestRate
      ? Number(interestRate?.replace("%", ""))
      : null,
    prepaymentPenaltyMonths: prepaymentPenalty ?? null,
    prepayPenaltyType,
    rateType,
    interestOnlyPeriod: !isEmptyValue(interestOnlyPeriod)
      ? parseInt(interestOnlyPeriod, 10)
      : null,
    totalOriginationAndDiscountPoints: totalOriginationAndDiscountPoints
      ? totalOriginationAndDiscountPoints?.replace("%", "")
      : null,
    interestReserve: interestReserve?.replace("$", "").replace(/,/g, ""),
    includeOutOfPocketBudgetInARLTV: includeOutOfPocketBudgetInARLTV ?? true,
    armType: isRateTypeFixed ? null : armType || null,
    armIndex: isRateTypeFixed ? null : armIndex || "1 Month Term SOFR",
    grossArmMargin: grossArmMargin
      ? Number(grossArmMargin?.replace("%", ""))
      : null,
    initialRateAdjustmentDate: isRateTypeFixed ? null : getFormattedDate(initialRateAdjustmentDate),
    initialPayAdjustmentDate: isRateTypeFixed ? null : getFormattedDate(initialPayAdjustmentDate),
    initialPeriodicCap: initialPeriodicCap
      ? Number(initialPeriodicCap?.replace("%", ""))
      : null,
    subsequentPeriodicCap: subsequentPeriodicCap
      ? Number(subsequentPeriodicCap?.replace("%", ""))
      : null,
    lifeTimeCap: lifeTimeCap
      ? Number(lifeTimeCap?.replace("%", ""))
      : null,
    lifeTimeFloor: lifeTimeFloor
      ? Number(lifeTimeFloor?.replace("%", ""))
      : null,
  };
}

function buildLoanInfoPayload(
  loanInformation: any,
  selectedCrossValue: boolean | undefined
) {
  const {
    loanStructure,
    loanPurpose,
    cashoutFlag,
    recourse,
    originationDate,
    expectedClosingDate,
    borrowerProceeds,
    cashOutRefinance,
    numberOfProperties,
    predominantState,
    pledgeOfEquity,
    predominantPropertyType,
    condoEligibility,
    loanTerm,
    calculatedCondoEligibility,
    exitStrategy,
    isPermitApprovalsAndFullPlansInPlace,
    irFundingSource,
    sizerRateLockPeriod,
    primaryLoanId,
    creditEvent
  } = loanInformation;

  return {
    loanStructure: loanStructure || null,
    loanPurpose: loanPurpose || null,
    cashoutFlag: cashoutFlag || null,
    recourseString: recourse,
    pledgeOfEquity: yesornToBoolean(pledgeOfEquity),
    isPermitApprovalsAndFullPlansInPlace,
    expectedClosingDate: expectedClosingDate ? expectedClosingDate : null,
    irFundingSource,
    sizerRateLockPeriod,
    borrowerProceeds: borrowerProceeds || cashOutRefinance || null,
    noOfProperties: Number(numberOfProperties) || null,
    predominantState,
    predominantPropertyType,
    condoEligibility,
    originationDate: originationDate ? originationDate : null,
    loanTermMonthly: Number(loanTerm) || null,
    calculatedCondoEligibility,
    exitStrategy: isEmptyValue(exitStrategy) ? null : exitStrategy,
    primaryLoanId,
    // takeoutPartnerId,
    crossCollaterlization: selectedCrossValue,
    creditEvent
  };
}

function applyCondoEligibilityDetails(
  loanRequestObject: any,
  loanCondo: any,
  calculatedCondo: string | undefined,
  calculatedCondoEligibility: any,
  condoEligibility: any
) {
  loanRequestObject.loanInfo.calculatedCondoEligibility =
    calculatedCondo || calculatedCondoEligibility;
  loanRequestObject.loanInfo.condoEligibility =
    calculatedCondo || condoEligibility;
  loanRequestObject.loanCondominium = {
    borrowerName: loanCondo?.borrowerName,
    monthlyHOAFee:
      loanCondo?.monthlyHOAFee !== null
        ? loanCondo?.monthlyHOAFee?.replace("$", "")
        : null,
    hoaDuesAboveFifteen:
      loanCondo?.hoaDuesAboveFifteen !== null
        ? loanCondo?.hoaDuesAboveFifteen === "Yes"
        : null,
    hoaDuesAboveTwenty:
      loanCondo?.hoaDuesAboveTwenty !== null
        ? loanCondo?.hoaDuesAboveTwenty === "Yes"
        : null,
    reserveFundsForOneYear:
      loanCondo?.reserveFundsForOneYear !== null
        ? loanCondo?.reserveFundsForOneYear === "Yes"
        : null,
    reserveFundsForTwoYears:
      loanCondo?.reserveFundsForTwoYears !== null
        ? loanCondo?.reserveFundsForTwoYears === "Yes"
        : null,
    isProjectCompleted:
      loanCondo?.isProjectCompleted !== null
        ? loanCondo?.isProjectCompleted === "Yes"
        : null,
    subjectToAdditionalPhasing:
      loanCondo?.subjectToAdditionalPhasing !== null
        ? loanCondo?.subjectToAdditionalPhasing === "Yes"
        : null,
    soldPercentage:
      loanCondo?.soldPercentage !== null
        ? loanCondo?.soldPercentage?.replace("%", "") / 100 || null
        : null,
    hoaUnderOwnerControl:
      loanCondo?.hoaUnderOwnerControl !== null
        ? loanCondo?.hoaUnderOwnerControl === "Yes"
        : null,
    simpleEstateOwnership:
      loanCondo?.simpleEstateOwnership !== null
        ? loanCondo?.simpleEstateOwnership === "Yes"
        : null,
    ownerOccupiedUnits: Number(loanCondo?.ownerOccupiedUnits) || null,
    renterOccupiedUnits: Number(loanCondo?.renterOccupiedUnits) || null,
    renterOccupiedPercentage:
      loanCondo?.renterOccupiedPercentage !== null
        ? loanCondo?.renterOccupiedPercentage?.replace("%", "") / 100 || null
        : null,
    individualOwnershipAboveTwentyFive:
      loanCondo?.individualOwnershipAboveTwentyFive !== null
        ? loanCondo?.individualOwnershipAboveTwentyFive === "Yes"
        : null,
    masterAssurancePolicy:
      loanCondo?.masterAssurancePolicy !== null
        ? loanCondo?.masterAssurancePolicy === "Yes"
        : null,
    projectHasMobileHomes:
      loanCondo?.projectHasMobileHomes !== null
        ? loanCondo?.projectHasMobileHomes === "Yes"
        : null,
    otherSourcesIncomeMoreThanTwenty:
      loanCondo?.otherSourcesIncomeMoreThanTwenty !== null
        ? loanCondo?.otherSourcesIncomeMoreThanTwenty === "Yes"
        : null,
    commercialPurposesAreaMoreThanForty:
      loanCondo?.commercialPurposesAreaMoreThanForty !== null
        ? loanCondo?.commercialPurposesAreaMoreThanForty === "Yes"
        : null,
    restrictOwnerToRent:
      loanCondo?.restrictOwnerToRent !== null
        ? loanCondo?.restrictOwnerToRent === "Yes"
        : null,
    documentsWithSEC:
      loanCondo?.documentsWithSEC !== null
        ? loanCondo?.documentsWithSEC === "Yes"
        : null,
    hoawithLitOrBankruptcy:
      loanCondo?.hoawithLitOrBankruptcy !== null
        ? loanCondo?.hoawithLitOrBankruptcy === "Yes"
        : null,
    ownerControlStartDate:
      loanCondo?.ownerControlStartDate !== ""
        ? loanCondo?.ownerControlStartDate
        : null,
    indivdualOwnedUnits: Number(loanCondo?.indivdualOwnedUnits),
    hoawithLitOrBankruptcyDesc: loanCondo?.hoawithLitOrBankruptcyDesc,
    restrictOwnerToRentOutDesc: loanCondo?.restrictOwnerToRentOutDesc,
    condoProjectName: loanCondo?.condoProjectName,
    streetAddressLine1: loanCondo?.streetAddressLine1,
    noOfUnits: Number(loanCondo?.noOfUnits),
    state: loanCondo?.state,
    city: loanCondo?.city,
    postalCode: loanCondo?.postalCode,
  };
}

export function getLoanRequestObject(
  createLoanParams: any,
  loanId: any,
  loanType: string,
  condoEligibSave?: boolean,
  calculatedCondo?: string
) {
  const originatorInfo = JSON.parse(getCookie("originatorData") || "null");

  const loanInformation = createLoanParams?.loanInformation || {};
  const { crossCollaterlization } = loanInformation;

  const loanEconomics = createLoanParams?.loanEconomics || {};
  const { rehabBudget, rateType } = loanEconomics;

  const borrowerInformation = createLoanParams?.borrowerInformation || {};

  const loanCondo = createLoanParams?.loanCondo || {};
  const loanDetailId = createLoanParams?.loanDetailId || {};
  const dueDiligencePartyInfo = createLoanParams?.dueDiligencePartyInfo || {};
  const loanSizerType = createLoanParams?.loanSizerType || "";
  const loanConfig = createLoanParams?.loanConfig || {};

  const borrowerInfo: any[] = [];
  borrowerInfo.push(buildBorrowerInfoEntry(borrowerInformation));
  const loanUserMap = [...borrowerInfo];

  const selectedCrossValue = getCrossCollaterlizationValue(
    crossCollaterlization
  );

  const rehabBudgetValue =
    loanSizerType === LoanSizerEnum.multifamilyMixedUse
      ? totalRehabBudgeFormula({ loanDetails: createLoanParams })
      : rehabBudget;
  const isRateTypeFixed = rateType === "Fixed";
  const loanRequestObject = {
    loanId: loanId.toString(),
    loanTypeId: 2,
    loanSizerType,
    loanType,
    loanExcelJson: "",
    // loanState: "in-progress",
    loanEconomics: buildLoanEconomicsPayload(
      loanEconomics,
      createLoanParams,
      rehabBudgetValue,
      isRateTypeFixed
    ),
    loanInfo: buildLoanInfoPayload(loanInformation, selectedCrossValue),
    loanUserMap,

    loanConfig: loanConfig ? loanConfig : {},
    loanCondominium: {},
    loanDetailId,
    dueDiligencePartyInfo,
    originatorInfo,
  };

  if (condoEligibSave === true) {
    applyCondoEligibilityDetails(
      loanRequestObject,
      loanCondo,
      calculatedCondo,
      loanInformation.calculatedCondoEligibility,
      loanInformation.condoEligibility
    );
  }
  return loanRequestObject;
}

export function downLoadCondo(
  createLoanParams: any,
  loanId: any,
  loanType: string
): Promise<any> {
  return new Promise((resolve, reject) => {
    // const url = getURL("public-api/aggregate/loans/condo");
    const loanRequestObject = getLoanRequestObject(
      createLoanParams,
      loanId,
      loanType,
      true
    );
    const requestObject = loanRequestObject;

    postDownloadCondo(requestObject)
      .then((res: { data: any }) => resolve(res.data))
      .catch((e: any) => {
        reject(e);
      });
  });
}

export function exportHTML({
  loanDetails,
  loanId,
  loanType,
}: {
  loanDetails: any;
  loanId: any;
  loanType: any;
}) {
  downLoadCondo(loanDetails, loanId, loanType).then((response) => {
    const source = response;
    const fileDownload = document.createElement("a");
    document.body.appendChild(fileDownload);
    fileDownload.href = source;
    fileDownload.download = "condoEligibility.doc";
    fileDownload.click();
    document.body.removeChild(fileDownload);
  });
}

const largestDataPoint = "300px";

export const getEligiblePartnersCols = (partnerList: ObjectType[]) => {
  const cols: ObjectType[] = [
    {
      id: "empty",
      title: "",
      headStyle: {
        padding: "0",
        fontWeight: "600",
        borderRight: "1px solid #dedede",
        minWidth: "300px",
        maxWidth: "300px",
      },
    },
  ];
  const sortedPartnerList: any = partnerList?.sort(
    (partner1: any, partner2: any) =>
      partner1.criticalFailed - partner2.criticalFailed
  );
  // eslint-disable-next-line no-unused-expressions
  sortedPartnerList?.forEach((partnerData: ObjectType, index: number) => {
    const partnerId = partnerData.key || partnerData.id;
    cols.push({
      id: partnerId,
      title: partnerData.programName,
      failedCount: partnerData.failedCount,
      criticalFailed: partnerData.criticalFailed,
      // maxWidth: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      width: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      padding: 0,
      "data-testid": `partner-pricing-data-${partnerData.label}`,
      headStyle: {
        // padding: "1rem",
        fontWeight: "600",
        textAlign: "center",
        borderRight: "1px solid #dedede",
      },
    });
  });
  return cols;
};

export const getfinalPricingCols = (partnerList: ObjectType[]) => {
  const cols: ObjectType = [
    {
      id: "label",
      title: "Final Toorak Pricing",
      "data-testid": "final-toorak-pricing",
      // maxWidth: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      width: `${largestDataPoint}`,
      headStyle: {
        // padding: "1rem",
        fontWeight: "600",
      },
      style: {
        // padding: "1rem",
        borderRight: "1px solid #dedede",
      },
    },
  ];

  partnerList.forEach((partnerData: ObjectType, index: number) => {
    const partnerId = partnerData.key || partnerData.id;
    // if (index <= 0) {
    cols.push({
      id: partnerId,
      "data-testid": `final-toorak-pricing-${partnerId}`,
      title: "",
      width: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      // maxWidth: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      // minWidth: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      headStyle: {
        // padding: "1rem",
        fontWeight: "600",
      },
      style: {
        // padding: "1rem",
        borderRight: "1px solid #dedede",
        textAlign: "center",
      },
    });
    // }
  });
  return cols;
};

export const getPricingAdjustmentsCols = (partnerList: ObjectType[]) => {
  const cols: ObjectType = [
    {
      id: "label",
      title: "Pricing Adjustments",
      "data-testid": "pricing-adjustment",
      width: `${largestDataPoint}`,
      // maxWidth: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      headStyle: {
        // padding: "1rem",
        fontWeight: "600",
      },
      style: {
        // padding: "1rem",
        borderRight: "1px solid #dedede",
      },
    },
  ];

  partnerList.forEach((partnerData: ObjectType, i: number) => {
    const partnerId = partnerData.key || partnerData.id;
    // if (i <= 0) {
    cols.push({
      id: partnerId,
      title: "",
      "data-testid": `pricing-adjustment-${partnerId}`,
      // minWidth: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      // maxWidth: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      width: `calc((100% - ${largestDataPoint}) / ${partnerList.length})`,
      headStyle: {
        // padding: "1rem",
        fontWeight: "600",
      },
      style: {
        // padding: "1rem",
        borderRight: "1px solid #dedede",
        textAlign: "center",
      },
    });
    // }
  });
  return cols;
};

const getRateLockExpiryDate = (
  rateLockExpiryDate: number,
  lockInPeriod: number,
  extendByDays: number = 0
) => {
  const expiry = moment
    .utc(rateLockExpiryDate)
    .add(parseInt(`${extendByDays}`, 10), "days");
  const expiryDate = `${expiry.format("ddd, MMM DD, YYYY")}`;

  return `${expiryDate} | ${lockInPeriod} Days`;
};

export const getRowsData = (
  fields: ObjectType[],
  partnersList: ObjectType[]
) => {
  return fields
    .filter(
      (fieldVal: ObjectType) =>
        (fieldVal?.hideCondition && fieldVal?.hideCondition()) ||
        !fieldVal?.hideCondition
    )
    .map((fieldData: ObjectType) => {
      let rowData: ObjectType = {
        label: fieldData.label,
      };
      partnersList?.forEach((partnerData: ObjectType) => {
        const partnerId = partnerData.key || partnerData.id;
        rowData = {
          ...rowData,
          [partnerId]:
            (fieldData.label !== "Pre/Post Close Rate Lock Expiry"
              ? partnerData.data?.[fieldData.field]
              : partnerData.data?.[fieldData.field.split(",")[0]] &&
                getRateLockExpiryDate(
                  partnerData.data?.[fieldData.field.split(",")[0]],
                  Number.parseInt(
                    partnerData.data?.[fieldData.field.split(",")[1]] || 0,
                    10
                  )
                )) ?? "--",
        };
      });
      return rowData;
    });
};

export const getLoanPricingFields = (
  version: string,
  additionalData?: ObjectType
) => {
  const versionToFieldsmap: ObjectType = {
    RATE: [
      {
        label: "Toorak Interest Rate",
        field: "toorakInterestRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.toorakInterestRate`,
        hideCondition: (data: any) => !data?.loanPricing?.toorakInterestRate,
      },
      {
        label: "Base Rate",
        field: "baseRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.baseRate`,
        hideCondition: (data: any) => !data?.loanPricing?.baseRate,
      },
      {
        label: "Base Price",
        field: "basePrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.basePrice`,
        hideCondition: (data: any) => !data?.loanPricing?.basePrice,
      },
      {
        label: "Actual Interest Rate",
        field: "actualInterestRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        overRideinfoContent: `data.loan.loanEconomics.interestRateAsOfCutOffDate`,
      },
      {
        label: "Variance From TRK Int. Rate",
        field: "interestRateVariance",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.interestRateVariance`,
        hideCondition: (data: any) => !data?.loanPricing?.interestRateVariance,
      },
      {
        label: "Price Adjustment",
        field: "priceAdjustment",
        fieldType: "",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.priceAdjustment`,
        hideCondition: (data: any) => !data?.loanPricing?.priceAdjustment,
      },
      {
        label: "Prepayment Penalty - Max Price",
        field: "pppMaxPrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.pppMaxPrice`,
        hideCondition: (data: any) => !data?.loanPricing?.pppMaxPrice,
      },
      {
        label: "State PPP Restriction - Max Price",
        field: "statepppRestrictionMaxPrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.statepppRestrictionMaxPrice`,
        hideCondition: (data: any) =>
          !data?.loanPricing?.statepppRestrictionMaxPrice,
      },
      {
        label: "Final Toorak Price",
        field: "finalToorakPrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.finalToorakPrice`,
        hideCondition: (data: any) => !data?.loanPricing?.finalToorakPrice,
      },
      {
        label: "Morgan Stanley Lease Status",
        field: "leaseStatus",
        fieldType: "dropdown",
        loanStage: [LoanStage.pre, LoanStage.post],
        infoContent: `loanPricing.leaseStatus`,
        dropDownValues: additionalData?.msLeaseStatusDropdownValue,
        hideCondition: (data: any) =>
          !data?.loanPricing?.leaseStatus &&
          !!additionalData?.msLeaseStatusDropdownValue,
      },
    ],
    "": [
      {
        label: "Final Toorak Price",
        field: "finalToorakPrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.finalToorakPrice`,
        hideCondition: (data: any) => !data?.loanPricing?.finalToorakPrice,
      },
      {
        label: "Price Adj. - Total",
        field: "totalPriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.totalPriceAdjustment`,
      },
      {
        label: "Base Price",
        field: "basePrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.basePrice`,
        hideCondition: (data: any) => !data?.loanPricing?.basePrice,
      },
      {
        label: "Prepayment Penalty - Max Price",
        field: "pppMaxPrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.pppMaxPrice`,
        hideCondition: (data: any) => !data?.loanPricing?.pppMaxPrice,
      },
      {
        label: "State PPP Restriction - Max Price",
        field: "statepppRestrictionMaxPrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.statepppRestrictionMaxPrice`,
      },
      {
        label: "Max Toorak Price",
        field: "maxAllowedToorakPrice",
        fieldType: "numeric",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.maxAllowedToorakPrice`,
        hideCondition: (data: any) =>
          data?.takeoutPartnerId === "c096ff03-b4c3-4691-bad9-aa448c328e7c" ||
          !data,
      },
      {
        label: "Morgan Stanley Lease Status",
        field: "leaseStatus",
        fieldType: "dropdown",
        loanStage: [LoanStage.pre, LoanStage.post],
        infoContent: `loanPricing.leaseStatus`,
        dropDownValues: additionalData?.msLeaseStatusDropdownValue,
        hideCondition: (data: any) =>
          !data?.loanPricing?.leaseStatus &&
          !!additionalData?.msLeaseStatusDropdownValue,
      },
    ],
  };
  return versionToFieldsmap?.[version] ?? versionToFieldsmap[""];
};

export const labelFieldName30year = (version: string) => {
  const versionToFieldsMap: ObjectType = {
    RATE: [
      {
        label: "Borrower FICO Pricing Bucket",
        field: "borrowerFico",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.borrowerFico`,
      },
      // {
      //   label: "Actual Leverage - LTV/LTC/LTP",
      //   field: "actualLeverage",
      //   fieldType: "percentage",
      //   loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
      //   infoContent: `toorakProceeds.actualLeverage`,
      // },
      {
        label: "Base FICO / Leverage Adjusted Rate",
        field: "baseFicoLeverageBucket",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.baseFicoLeverageBucket`,
      },
      {
        label: "Rate Adj. - Prepayment Penalty",
        field: "prePaymentPenalty",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.prePaymentPenalty`,
      },
      {
        label: "Rate Adj. - Interest Only",
        field: "interestOnlyRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.interestOnlyRate`,
      },
      {
        label: "Rate Adj. - DSCR",
        field: "dscrRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.dscrRate`,
      },
      {
        label: "Rate Adj. - Short Term Rental",
        field: "shortTermRentalRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.shortTermRentalRate`,
      },
      {
        label: "Rate Adj. - Cash Out Refinance",
        field: "cashOutRefinanceRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.cashOutRefinanceRate`,
      },
      {
        label: "Rate Adj. - Property Type",
        field: "propertyTypeRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.propertyTypeRate`,
      },
      {
        label: "Rate Adj. - Loan Balance",
        field: "loanBalanceRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.loanBalanceRate`,
      },
      {
        label: "Rate Adj. - ARM",
        field: "amortizationRate",
        fieldType: "percentage",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.amortizationRate`,
      },
      {
        label: "Leverage Pricing Bucket",
        field: "leveragePricingBucket",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.leveragePricingBucket`,
      },
      {
        label: "Loan Balance Bucket",
        field: "loanBalanceBucket",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.loanBalanceBucket`,
      },
      // {
      //   label: "After Adjustment Min Proceeds",
      //   field: "afterAdjustmentMinProceeds",
      //   fieldType: "currency",
      //
      //   loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
      //   infoContent: `toorakProceeds.afterAdjustmentMinProceeds`
      // },
      // {
      //   label: "Originator Proceeds",
      //   field: "originatorProceeds",
      //   fieldType: "currency",
      //
      //   loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
      //   infoContent: `toorakProceeds.originatorProceeds`
      // },
      // {
      //   label: "After Adjustment Cost Proceeds",
      //   field: "afterAdjustmentCostProceeds",
      //   fieldType: "currency",
      //
      //   loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
      //   infoContent: `toorakProceeds.afterAdjustmentCostProceeds`
      // },

      // {
      //   label: "Balance Variance",
      //   field: "balanceVariance",
      //   fieldType: "currency",
      //
      //   loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
      //   infoContent: `toorakProceeds.balanceVariance`
      // },
    ],
    "": [
      {
        label: "Borrower FICO Pricing Bucket",
        field: "borrowerFico",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `toorakProceeds.borrowerFico`,
      },
      // {
      //   label: "Actual Leverage - LTV/LTC/LTP",
      //   field: "actualLeverage",
      //   fieldType: "percentage",
      //   loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
      //   infoContent: `toorakProceeds.actualLeverage`,
      // },
      {
        label: "Price Adj. - FICO",
        field: "ficoPriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.ficoPriceAdjustment`,
      },
      {
        label: "Price Adj. - Pre-payment Penalty Term",
        field: "penaltyTermPriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.penaltyTermPriceAdjustment`,
      },
      {
        label: "Price Adj. - Loan Exceptions",
        field: "loanExceptionsPriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.loanExceptionsPriceAdjustment`,
      },
      {
        label: "Price Adj. - DSCR",
        field: "dscrPriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.dscrPriceAdjustment`,
      },
      {
        label: "Price Adj. - Loan Term",
        field: "shortTermRentalRate",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.shortTermRentalRate`,
      },
      {
        label: "Price Adj. - Loan Purpose",
        field: "loanPurposePriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.loanPurposePriceAdjustment`,
      },
      {
        label: "Price Adj. - Property Type",
        field: "propertyTypePriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.propertyTypePriceAdjustment`,
      },
      {
        label: "Price Adj. - FN/NPRA",
        field: "loanUserTypePriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.loanUserTypePriceAdjustment`,
      },
      {
        label: "Price Adj. - Loan Balance",
        field: "loanBalancePriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.loanBalancePriceAdjustment`,
      },
      {
        label: "Price Adj. - Cross Collateralized",
        field: "crossCollaterizedAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.crossCollaterizedAdjustment`,
      },
      {
        label: "Price Adj. - Amortization",
        field: "amortizationPriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.amortizationPriceAdjustment`,
      },
      {
        label: "Leverage Pricing Bucket",
        field: "leveragePricingBucket",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.leveragePricingBucket`,
      },
      {
        label: "Loan Balance Bucket",
        field: "loanBalanceBucket",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.loanBalanceBucket`,
      },
      {
        label: "Price Adj. - Tier state",
        field: "propertyLocationPriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.loanBalanceBucket`,
      },
      {
        label: "FC/SS/DIL/BK7 36 - 47mo",
        field: "creditEventPriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.creditEventPriceAdjustment`,
      },
      {
        label: "Florida State Adjustment",
        field: "statePriceAdjustment",
        fieldType: "string",
        loanStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
        infoContent: `loanPricing.statePriceAdjustment`,
      },
    ],
  };
  return versionToFieldsMap?.[version] ?? versionToFieldsMap?.[""];
};

export const getOriginatorLabel = (label: any, creditLines: ObjectType[]) => {
  const creditLineObj = creditLines.find((cdl: any) => cdl.fullName === label);
  return creditLineObj?.programName ?? label;
};

export const sanitizeLoanDataForTermSheet = (value: any) => {
  const tempValue = `${value}`?.replace(/(\$|%|months)/gi, "");
  return !isNaN(Number(tempValue)) &&
    /^\d+(\.\d{1,4})?$/.test(tempValue.toString())
    ? Number(tempValue)?.toFixed(2)
    : tempValue;
};

export const newLoanTypes = {
  bridgeLoan: "Bridge Loan",
  dscrLoan: "30 Year Loan",
};

export function capitalizeFirstLetterOfEachWord(str: string) {
  return str
    .split(" ") // Split the string into an array of words
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter and concatenate with the rest of the word
    .join(" "); // Join the array back into a string
}

export const getLoanType = {
  0: {
    name: "Bridge Loan",
    displayValue: "BridgeLoan",
    loanTypeId: 2,
    loanCreationName: "Bridge Loan",
    settlementTabPath: "Bridge",
  },
  1: {
    name: "Investor DSCR",
    displayValue: "InvestorDSCR",
    loanTypeId: 1,
    loanCreationName: "30 Year Loan",
    settlementTabPath: "DSCR",
  },
  2: {
    name: "2 + 1 Loan",
    displayValue: "TwoPlusOneLoan",
    loanTypeId: 3,
    loanCreationName: "2 + 1 Loan",
  },
  3: {
    name: "Bridge Toorval Loan",
    displayValue: "ToovalBridge",
    loanTypeId: 4,
    loanCreationName: "Toorval Bridge Loan",
  },
};

// Escapes regex metacharacters in a value before it's interpolated into a
// dynamically-built RegExp source string (e.g. in cleanedAdressString
// below). Without this, a postalCode/city/state value containing regex
// syntax would be compiled as part of the pattern itself rather than
// matched as a literal string — a regex-injection risk, and, depending on
// what a caller puts there, a potential source of a genuinely catastrophic
// (nested-quantifier) pattern rather than just an overlapping-class one.
function escapeRegExpLiteral(value: string): string {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

// Format required Adress String
export const cleanedAdressString = (propertyLocation: any) => {
  if (!propertyLocation) {
    return "";
  }
  const postalCode = escapeRegExpLiteral(String(propertyLocation?.postalCode ?? ""));
  const city = escapeRegExpLiteral(String(propertyLocation?.city ?? ""));
  const state = escapeRegExpLiteral(String(propertyLocation?.state ?? ""));
  const pattern = new RegExp(String.raw`\b(${postalCode}|${city}|${state})\b`, "g");
  // Remove matches from the address
  const cleanedAddress = propertyLocation?.address
    ?.replace(pattern, "")
    ?.trim()
    ?.replace(/\s+/g, " ");
  const address = propertyLocation?.address
    ? `${cleanedAddress}, ${propertyLocation?.city}, ${propertyLocation?.state} ${propertyLocation?.postalCode}`
    : propertyLocation?.postalCode
    ? `${propertyLocation?.city}, ${propertyLocation?.state} ${propertyLocation?.postalCode}`
    : "";
  return address;
};

export const calculateCurrentSelectedInterestRate = (
  currentRateData: any,
  waiverRequest: any,
  buyDownOptions: any
) => {
  const currentSelectedInterestRate = { ...currentRateData };
  const activeWaiverRequestStatus = waiverRequest?.data?.data?.find(
    (item: any) => item?.data?.isActive
  )?.data?.waiverStatus;

  const requestedWaiver = waiverRequest?.data?.data?.find(
    (item: any) =>
      item?.data?.waiverStatus === "Requested" && item?.data?.isActive
  );
  const approvedWaivers = waiverRequest?.data?.data?.filter(
    (item: any) => item?.data?.waiverStatus === "Approved"
  );
  const latestUpdatedRecord =
    approvedWaivers
      ?.filter((waiver: any) => waiver?.updatedAt) // Filter out records without updatedAt
      ?.sort(
        (a: any, b: any) =>
          new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
      )[0] ?? {};

  if (activeWaiverRequestStatus === "Rejected") {
    const tempCurrentRateData = buyDownOptions?.find(
      (item: any) =>
        item?.interestRate * 100 ===
        parseFloat(currentSelectedInterestRate?.interestRate?.replace("%", ""))
    );

    if (
      approvedWaivers?.length > 0 &&
      Object.keys(latestUpdatedRecord ?? {}).length
    ) {
      return calculateSubtractionOfCurrentSelectedInterestRate(
        latestUpdatedRecord,
        currentRateData
      );
    }

    if (tempCurrentRateData) {
      currentRateData = {
        discountPoints:
          tempCurrentRateData?.discountPointPercentage?.toFixed(4) || "0.0000",
        discountPointsWithDollar:
          tempCurrentRateData?.discountPointAmount?.toFixed(2) || "0.00",
        interestRate: `${(tempCurrentRateData?.interestRate * 100)?.toFixed(
          3
        )}%`,
      };
    }
    // } else if (
    //   Object.keys(requestedWaiver ?? {}).length &&
    //   approvedWaivers?.length === 0
    // ) {
    //   const currentDiscountPoints =
    //     parseFloat(currentRateData?.discountPoints) || 0;
    //   const latestDiscountPoints =
    //     parseFloat(requestedWaiver?.data?.requestedData?.discountPoints) || 0;
    //   const discountPoints = Math.max(
    //     0,
    //     currentDiscountPoints - latestDiscountPoints
    //   ).toFixed(4);
    //   const discountPointsWithDollar = Math.max(
    //     0,
    //     parseFloat(
    //       currentRateData?.discountPointsWithDollar?.replace(/[^0-9.]/g, "")
    //     ) -
    //       parseFloat(
    //         requestedWaiver?.data?.requestedData?.discountPointsAmount?.replace(/[^0-9.]/g, "")
    //       )
    //   ).toFixed(2);

    //   currentRateData = {
    //     ...currentRateData,
    //     discountPoints,
    //     discountPointsWithDollar,
    //   };
  } else if (
    approvedWaivers?.length > 0 &&
    Object.keys(latestUpdatedRecord ?? {}).length
  ) {
    currentRateData = calculateSubtractionOfCurrentSelectedInterestRate(
      latestUpdatedRecord,
      currentRateData
    );
  }
  return { ...currentRateData };
};

const calculateSubtractionOfCurrentSelectedInterestRate = (
  latestUpdatedRecord: any,
  currentRateData: any
) => {
  const latestData = latestUpdatedRecord.data?.finalData || {};
  const currentDiscountPoints =
    parseFloat(currentRateData?.discountPoints) || 0;
  const latestDiscountPoints = parseFloat(latestData.discountPoints) || 0;

  const discountPoints = Math.max(
    0,
    currentDiscountPoints - latestDiscountPoints
  ).toFixed(4);
  const discountPointsWithDollar = Math.max(
    0,
    parseFloat(
      currentRateData?.discountPointsWithDollar?.replace(/[^0-9.]/g, "")
    ) - parseFloat(latestData.discountPointsAmount?.replace(/[^0-9.]/g, ""))
  ).toFixed(2);

  currentRateData = {
    ...currentRateData,
    discountPoints,
    discountPointsWithDollar,
  };
  return { ...currentRateData };
};

export const isWithinLast6Months = (inputDate?: string | null): boolean => {
  if (!inputDate) return false; // handles null / undefined / empty

  const givenDate = new Date(inputDate);

  // check invalid date
  if (isNaN(givenDate.getTime())) return false;

  const today = new Date();
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(today.getMonth() - 6);

  return givenDate >= sixMonthsAgo && givenDate <= today;
};

export const extractUnitsResultData = (data: any): any[] => {
  if (!data || typeof data !== "object") return [];

  return Object.keys(data)
    .filter((key) => key.startsWith("unit") && data[key])
    .map((key) => data[key]);
};